import React, { useEffect, useState } from "react";
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    Area, AreaChart, ReferenceLine,
} from "recharts";
import {
    Activity, TrendingUp, Shield, AlertTriangle, Zap, Coins,
    BarChart3, Cpu, DollarSign, Percent,
} from "lucide-react";
import { GuildCapitalLogo } from "../components/GuildCapitalLogo";

const GOLD = "#c9a227";
const GREEN = "#22c55e";
const RED = "#ef4444";
const BG = "#0a0a0a";
const CARD = "#141414";
const BORDER = "rgba(201,162,39,0.18)";

/* Real numbers from FULL STACK 5.5-year backtest (all 4 layers) */
const KPIS = {
    starting_capital: 10000,
    current_equity: 33284,
    total_return_pct: 232.84,
    annualized_pct: 24.43,
    max_drawdown_pct: -16.08,
    sharpe: 2.15,
    days_live: 2008,
    trades: 548,
};

const ASSETS = [
    { symbol: "BTC/USDT", alloc: 50, start: 5000, current: 8498, return: 69.96,
      trades: 133, regime: "Bull Trend", status: "active" },
    { symbol: "ETH/USDT", alloc: 30, start: 3000, current: 6253, return: 108.44,
      trades: 200, regime: "Bull Trend", status: "active" },
    { symbol: "SOL/USDT", alloc: 20, start: 2000, current: 2520, return: 26.02,
      trades: 215, regime: "Sideways", status: "active" },
];

const TARGET = {
    conservative: 25, target: 30, ambitious: 35,
    current: 10.44,
};

// Bot config constants — single source of truth so future changes are 1-line edits
const BOT_CONFIG = {
    STARTING_CAPITAL: 8801,      // fallback only — real cost basis today ($8k inception + $1,591 AVAX+DOT deposit − $790 reallocation on 18 Jul). Live value from stats.total_deposits.
                                 // Used only as fallback if stats API hasn't hydrated yet.
                                 // Post next capital injection, update this value AND append to CAPITAL_EVENTS in server.py.
    STARTING_CASH_POOL: 4000,      // 50% of capital starts as USDT (grid buy-side buffer)
    STARTING_GRID_LEVELS: 20,       // per asset
    KILL_SWITCH_PCT: -5,
};

const Card = ({ children, glow = false, ...props }) => (
    <div
        {...props}
        style={{
            background: CARD,
            border: `1px solid ${BORDER}`,
            borderRadius: 4,
            padding: 20,
            boxShadow: glow ? `0 0 24px rgba(201,162,39,0.08)` : "none",
            ...props.style,
        }}
    >
        {children}
    </div>
);

const Kicker = ({ children }) => (
    <div style={{
        color: GOLD, fontSize: 10, letterSpacing: "0.25em",
        textTransform: "uppercase", fontWeight: 500, marginBottom: 6,
    }}>{children}</div>
);

const KpiCard = ({ icon: Icon, label, value, sub, color = "#fff", trend, testid }) => {
    const trendColor = trend === undefined || trend === null ? "#666"
        : trend > 0.5 ? GREEN : trend < -0.5 ? RED : "#888";
    const trendSym = trend === undefined || trend === null ? ""
        : trend > 0.5 ? "▲" : trend < -0.5 ? "▼" : "◆";
    return (
        <Card data-testid={testid}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                <Icon size={18} color={GOLD} />
                {trend !== undefined && (
                    <span style={{ fontSize: 11, color: trendColor, fontWeight: 600 }}>
                        {trendSym} {Math.abs(trend).toFixed(2)}%
                    </span>
                )}
            </div>
            <Kicker>{label}</Kicker>
            <div style={{
                fontFamily: "Playfair Display, Georgia, serif",
                fontSize: 30, fontWeight: 400, color, lineHeight: 1, letterSpacing: "-0.5px",
            }}>{value}</div>
            {sub && <div style={{ color: "#888", fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", marginTop: 6 }}>{sub}</div>}
        </Card>
    );
};

const StatusPill = ({ status }) => {
    const map = {
        active: { bg: "rgba(34,197,94,0.15)", color: GREEN, text: "LIVE" },
        paused: { bg: "rgba(234,179,8,0.15)", color: "#eab308", text: "PAUSED" },
        killed: { bg: "rgba(239,68,68,0.15)", color: RED, text: "STOPPED" },
    }[status] || { bg: "#333", color: "#aaa", text: "?" };
    return (
        <span style={{
            background: map.bg, color: map.color, fontSize: 9, padding: "3px 8px",
            borderRadius: 2, letterSpacing: "0.15em", fontWeight: 700,
        }}>{map.text}</span>
    );
};

export default function BotDashboard() {
    const [curve, setCurve] = useState([]);
    const [now, setNow] = useState(new Date());
    const [loading, setLoading] = useState(true);
    const [live, setLive] = useState(null);
    const [liveCurve, setLiveCurve] = useState([]);
    const [stats, setStats] = useState(null);
    const [historyRange, setHistoryRange] = useState(24);
    const [copyStatus, setCopyStatus] = useState("");
    const [reportText, setReportText] = useState("");
    const [showReportModal, setShowReportModal] = useState(false);
    const [fills, setFills] = useState(null);
    const [restarts, setRestarts] = useState(null);
    const [sweepData, setSweepData] = useState(null);
    const [layerData, setLayerData] = useState(null);
    const [sweepModal, setSweepModal] = useState({ open: false, prefillAmount: "0" });
    const [sweepForm, setSweepForm] = useState({ amount: "", destination: "Aditya", destination_address: "", note: "" });
    const [sweepSubmitting, setSweepSubmitting] = useState(false);

    const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

    // Color helper: neutral for tiny movements, red/green only for meaningful moves
    const flatColor = (pct, threshold = 0.5) => {
        if (pct === undefined || pct === null || isNaN(pct)) return "#666";
        if (pct > threshold) return GREEN;
        if (pct < -threshold) return RED;
        return "#fff"; // essentially flat = white/neutral
    };

    useEffect(() => {
        fetch("/downloads/trading-bot/portfolio_data.json")
            .then((r) => r.json())
            .then((data) => { setCurve(data); setLoading(false); })
            .catch(() => setLoading(false));
        const t = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(t);
    }, []);

    useEffect(() => {
        const fetchLive = () => {
            fetch(`${BACKEND}/api/bot/live-lite`)
                .then((r) => r.json())
                .then((data) => setLive(data))
                .catch(() => setLive(null));
            fetch(`${BACKEND}/api/bot/fills`)
                .then((r) => r.json())
                .then((data) => setFills(data))
                .catch(() => setFills(null));
            fetch(`${BACKEND}/api/bot/restarts`)
                .then((r) => r.json())
                .then((data) => setRestarts(data))
                .catch(() => setRestarts(null));
            fetch(`${BACKEND}/api/bot/sweeps`)
                .then((r) => r.json())
                .then((data) => setSweepData(data))
                .catch(() => setSweepData(null));
            fetch(`${BACKEND}/api/bot/layers`)
                .then((r) => r.json())
                .then((data) => setLayerData(data))
                .catch(() => setLayerData(null));
        };
        fetchLive();
        const t = setInterval(fetchLive, 15000);
        return () => clearInterval(t);
    }, [BACKEND]);

    useEffect(() => {
        const fetchHistory = () => {
            fetch(`${BACKEND}/api/bot/history?hours=${historyRange}`)
                .then((r) => r.json())
                .then((data) => setLiveCurve(data.points || []))
                .catch(() => setLiveCurve([]));
            fetch(`${BACKEND}/api/bot/stats`)
                .then((r) => r.json())
                .then((data) => setStats(data))
                .catch(() => setStats(null));
        };
        fetchHistory();
        const t = setInterval(fetchHistory, 30000);
        return () => clearInterval(t);
    }, [BACKEND, historyRange]);

    // ─── Sweep submit handler ───
    const submitSweep = async () => {
        const amt = parseFloat(sweepForm.amount);
        if (!amt || amt <= 0) { alert("Enter a valid amount"); return; }
        setSweepSubmitting(true);
        try {
            const res = await fetch(`${BACKEND}/api/bot/sweeps`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    amount: amt,
                    destination: sweepForm.destination,
                    destination_address: sweepForm.destination_address || null,
                    note: sweepForm.note || "",
                    mode: "paper",
                }),
            });
            if (res.ok) {
                const refresh = await fetch(`${BACKEND}/api/bot/sweeps`).then(r => r.json());
                setSweepData(refresh);
                setSweepModal({ open: false, prefillAmount: "0" });
                setSweepForm({ amount: "", destination: "Aditya", destination_address: "", note: "" });
                setCopyStatus(`Sweep recorded: $${amt.toFixed(2)} → ${sweepForm.destination}`);
                setTimeout(() => setCopyStatus(""), 3000);
            } else { alert("Sweep failed. Check console."); }
        } catch (e) { alert("Sweep failed: " + e.message); }
        finally { setSweepSubmitting(false); }
    };

    useEffect(() => {
        if (sweepModal.open) {
            setSweepForm(f => ({ ...f, amount: sweepModal.prefillAmount }));
        }
    }, [sweepModal.open, sweepModal.prefillAmount]);

    return (
        <div style={{
            minHeight: "100vh", background: BG, color: "#ccc",
            fontFamily: "Inter, -apple-system, sans-serif", padding: "24px 32px",
        }} data-testid="bot-dashboard">

            {/* DAILY REPORT MODAL — bulletproof copy fallback */}
            {showReportModal && (
                <div
                    data-testid="report-modal"
                    onClick={() => setShowReportModal(false)}
                    style={{
                        position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)",
                        zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center",
                        padding: 24, backdropFilter: "blur(6px)",
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            background: CARD, border: `1px solid ${GOLD}`, borderRadius: 4,
                            padding: 28, maxWidth: 620, width: "100%", boxShadow: "0 0 40px rgba(201,162,39,0.25)",
                        }}
                    >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                            <div>
                                <div style={{ color: GOLD, fontSize: 10, letterSpacing: "0.3em", fontWeight: 700 }}>DAILY REPORT — READY TO SEND</div>
                                <div style={{ color: "#888", fontSize: 11, marginTop: 4 }}>Select all text below → copy → paste into WhatsApp</div>
                            </div>
                            <button
                                data-testid="close-report-modal-btn"
                                onClick={() => setShowReportModal(false)}
                                style={{ background: "transparent", border: `1px solid ${BORDER}`, color: "#888", padding: "4px 10px", borderRadius: 2, cursor: "pointer", fontSize: 11 }}
                            >✕</button>
                        </div>
                        <textarea
                            data-testid="report-text-area"
                            readOnly
                            value={reportText}
                            onFocus={(e) => e.target.select()}
                            style={{
                                width: "100%", minHeight: 320, background: "#0a0a0a",
                                color: "#e5e5e5", border: `1px solid ${BORDER}`, borderRadius: 3,
                                padding: 14, fontFamily: "monospace", fontSize: 12, lineHeight: 1.55,
                                resize: "vertical", outline: "none",
                            }}
                        />
                        <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
                            <button
                                data-testid="modal-copy-btn"
                                onClick={async () => {
                                    const ta = document.querySelector('[data-testid="report-text-area"]');
                                    if (ta) { ta.select(); ta.setSelectionRange(0, 999999); }
                                    let ok = false;
                                    try {
                                        if (navigator.clipboard && window.isSecureContext) {
                                            await navigator.clipboard.writeText(reportText); ok = true;
                                        }
                                    } catch { ok = false; }
                                    if (!ok) { try { ok = document.execCommand("copy"); } catch { ok = false; } }
                                    setCopyStatus(ok ? "copied" : "modal");
                                    setTimeout(() => setCopyStatus(""), 2500);
                                }}
                                style={{
                                    background: GOLD, border: "none", color: "#000", fontWeight: 700,
                                    padding: "10px 18px", borderRadius: 3, cursor: "pointer",
                                    fontSize: 11, letterSpacing: "0.15em", flex: 1,
                                }}
                            >{copyStatus === "copied" ? "✓ COPIED — PASTE TO WHATSAPP" : "📋 COPY TEXT"}</button>
                            <button
                                onClick={() => setShowReportModal(false)}
                                style={{
                                    background: "transparent", border: `1px solid ${BORDER}`,
                                    color: "#888", padding: "10px 18px", borderRadius: 3, cursor: "pointer",
                                    fontSize: 11, letterSpacing: "0.15em",
                                }}
                            >CLOSE</button>
                        </div>
                    </div>
                </div>
            )}

            {/* SWEEP MODAL — Record chips-off-table events for LP audit trail */}
            {sweepModal.open && (
                <div
                    data-testid="sweep-modal"
                    onClick={() => setSweepModal({ open: false, prefillAmount: "0" })}
                    style={{
                        position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)",
                        zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center",
                        padding: 24, backdropFilter: "blur(6px)",
                    }}
                >
                    <div onClick={(e) => e.stopPropagation()} style={{
                        background: CARD, border: `1px solid #22c55e`, borderRadius: 6,
                        padding: 28, maxWidth: 480, width: "100%",
                        boxShadow: "0 0 40px rgba(34,197,94,0.35)",
                    }}>
                        <div style={{ fontSize: 11, color: "#86efac", letterSpacing: 2, fontWeight: 700, marginBottom: 4 }}>RECORD SWEEP · CHIPS OFF THE TABLE</div>
                        <div style={{ fontSize: 20, color: "#fff", marginBottom: 20, fontFamily: "'Playfair Display', serif" }}>Confirm Withdrawal to Cold Wallet</div>

                        <label style={{ display: "block", fontSize: 11, color: "#9ca3af", marginBottom: 4, letterSpacing: 1 }}>AMOUNT (USDT)</label>
                        <input data-testid="sweep-amount"
                            type="number" step="0.01" value={sweepForm.amount}
                            onChange={(e) => setSweepForm({ ...sweepForm, amount: e.target.value })}
                            style={{ width: "100%", padding: "10px 12px", background: "#111", border: "1px solid #333", borderRadius: 4, color: "#fff", fontSize: 15, marginBottom: 14, fontFamily: "'Playfair Display', serif", fontWeight: 700 }} />

                        <label style={{ display: "block", fontSize: 11, color: "#9ca3af", marginBottom: 4, letterSpacing: 1 }}>DESTINATION</label>
                        <select data-testid="sweep-destination"
                            value={sweepForm.destination}
                            onChange={(e) => setSweepForm({ ...sweepForm, destination: e.target.value })}
                            style={{ width: "100%", padding: "10px 12px", background: "#111", border: "1px solid #333", borderRadius: 4, color: "#fff", fontSize: 14, marginBottom: 14 }}>
                            <option>Aditya</option>
                            <option>Martyn</option>
                            <option>Guild Ops</option>
                            <option>Roshni</option>
                            <option>Custom</option>
                        </select>

                        <label style={{ display: "block", fontSize: 11, color: "#9ca3af", marginBottom: 4, letterSpacing: 1 }}>TANGEM / WALLET ADDRESS (optional)</label>
                        <input data-testid="sweep-address"
                            type="text" value={sweepForm.destination_address}
                            onChange={(e) => setSweepForm({ ...sweepForm, destination_address: e.target.value })}
                            placeholder="0x... or bc1..."
                            style={{ width: "100%", padding: "10px 12px", background: "#111", border: "1px solid #333", borderRadius: 4, color: "#fff", fontSize: 13, marginBottom: 14, fontFamily: "monospace" }} />

                        <label style={{ display: "block", fontSize: 11, color: "#9ca3af", marginBottom: 4, letterSpacing: 1 }}>NOTE (optional)</label>
                        <input data-testid="sweep-note"
                            type="text" value={sweepForm.note}
                            onChange={(e) => setSweepForm({ ...sweepForm, note: e.target.value })}
                            placeholder="Weekly Friday sweep · school fees · etc"
                            style={{ width: "100%", padding: "10px 12px", background: "#111", border: "1px solid #333", borderRadius: 4, color: "#fff", fontSize: 13, marginBottom: 20 }} />

                        <div style={{ display: "flex", gap: 10 }}>
                            <button data-testid="sweep-submit-btn"
                                onClick={submitSweep} disabled={sweepSubmitting}
                                style={{ flex: 1, padding: "12px 18px", background: "#22c55e", color: "#000", border: "none", borderRadius: 4, cursor: "pointer", fontWeight: 700, fontSize: 12, letterSpacing: "0.15em" }}>
                                {sweepSubmitting ? "RECORDING..." : "✓ CONFIRM SWEEP"}
                            </button>
                            <button onClick={() => setSweepModal({ open: false, prefillAmount: "0" })}
                                style={{ padding: "12px 18px", background: "transparent", border: `1px solid ${BORDER}`, color: "#888", borderRadius: 4, cursor: "pointer", fontSize: 11, letterSpacing: "0.15em" }}>
                                CANCEL
                            </button>
                        </div>
                        <div style={{ fontSize: 10, color: "#6b7280", marginTop: 12, textAlign: "center", fontStyle: "italic" }}>
                            Paper mode: records event only. Post-Aug-1 patch wires real Bybit withdraw() to Tangem.
                        </div>
                    </div>
                </div>
            )}

            {/* GUILD CAPITAL — HERO MARK */}
            <div style={{
                display: "flex", flexDirection: "column", alignItems: "center",
                marginTop: 32, marginBottom: 32, paddingTop: 40, paddingBottom: 24,
                borderBottom: `1px solid ${BORDER}`, overflow: "visible",
            }}>
                <GuildCapitalLogo size={220} />
                <div style={{
                    color: "#6b7280", fontSize: 10, letterSpacing: "0.45em",
                    textTransform: "uppercase", marginTop: 14, fontWeight: 500,
                }}>
                    Institutional Quant Systems
                </div>
            </div>

            {/* HEADER */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 32 }}>
                <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div style={{
                            width: 40, height: 40, border: `1.5px solid ${GOLD}`, borderRadius: "50%",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontFamily: "Playfair Display, serif", color: GOLD, fontSize: 20,
                        }}>Q</div>
                        <div>
                            <h1 style={{
                                fontFamily: "Playfair Display, Georgia, serif",
                                color: "#fff", fontSize: 22, fontWeight: 400, margin: 0, letterSpacing: 0.5,
                            }}>MIDAS TOUCH <span style={{ color: GOLD }}>· The Guild Quant Engine</span></h1>
                            <div style={{ color: GOLD, fontSize: 9, letterSpacing: "0.35em", textTransform: "uppercase", marginTop: 2 }}>
                                Multi-Asset Grid · Regime-Adaptive · Institutional-Grade
                            </div>
                        </div>
                    </div>
                </div>
                <div style={{ textAlign: "right" }}>
                    <div style={{ color: "#888", fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase" }}>
                        {now.toISOString().slice(0, 10)} · {now.toISOString().slice(11, 19)} UTC
                    </div>
                    <div style={{ marginTop: 6, display: "flex", gap: 8, justifyContent: "flex-end", alignItems: "center" }}>
                        <StatusPill status="active" />
                        {(() => {
                            const snap = live?.snapshot || {};
                            const mode = (snap.mode || "paper").toUpperCase();
                            const isLiveMode = mode === "LIVE";
                            return (
                                <span
                                    data-testid="mode-badge"
                                    style={{
                                        background: isLiveMode ? "rgba(239,68,68,0.15)" : "rgba(201,162,39,0.1)",
                                        color: isLiveMode ? RED : GOLD,
                                        fontSize: 9, padding: "3px 8px", borderRadius: 2,
                                        letterSpacing: "0.15em", fontWeight: 700,
                                        border: isLiveMode ? `1px solid ${RED}` : "1px solid transparent",
                                    }}
                                >
                                    {isLiveMode ? "● LIVE MONEY" : "PAPER TRADING"}
                                </span>
                            );
                        })()}
                        {live?.snapshot?.halal_mode && (
                            <span
                                data-testid="halal-mode-badge"
                                title="Sharia-Compliant: futures/margin/lending are hard-blocked at bot layer. Zakat auto-tracked."
                                style={{
                                    background: "rgba(16,185,129,0.15)",
                                    color: "#10b981",
                                    fontSize: 9, padding: "3px 8px", borderRadius: 2,
                                    letterSpacing: "0.15em", fontWeight: 700,
                                    border: "1px solid rgba(16,185,129,0.5)",
                                    display: "inline-flex", alignItems: "center", gap: 4,
                                }}
                            >
                                <span style={{ fontSize: 11, lineHeight: 1 }}>☾</span>
                                HALAL-COMPLIANT
                            </span>
                        )}
                        <button
                            data-testid="copy-daily-report-btn"
                            onClick={async () => {
                                try {
                                    const r = await fetch(`${BACKEND}/api/bot/daily-digest`);
                                    const d = await r.json();
                                    if (!d.message_text) { setCopyStatus("error"); setTimeout(() => setCopyStatus(""), 2500); return; }
                                    setReportText(d.message_text);
                                    setShowReportModal(true);
                                    // Try silent clipboard copy — if it works, great; if not, modal still shows
                                    try {
                                        if (navigator.clipboard && window.isSecureContext) {
                                            await navigator.clipboard.writeText(d.message_text);
                                            setCopyStatus("copied");
                                        } else { setCopyStatus("modal"); }
                                    } catch { setCopyStatus("modal"); }
                                    setTimeout(() => setCopyStatus(""), 3000);
                                } catch { setCopyStatus("error"); setTimeout(() => setCopyStatus(""), 2500); }
                            }}
                            style={{
                                background: copyStatus === "copied" ? GREEN : "transparent",
                                border: `1px solid ${copyStatus === "copied" ? GREEN : GOLD}`,
                                color: copyStatus === "copied" ? "#000" : GOLD,
                                fontSize: 9, padding: "4px 10px", borderRadius: 2,
                                letterSpacing: "0.15em", fontWeight: 700, cursor: "pointer",
                                transition: "all 0.2s",
                            }}
                        >
                            {copyStatus === "copied" ? "✓ COPIED — MODAL OPEN" : copyStatus === "error" ? "ERROR — RETRY" : copyStatus === "modal" ? "📋 SELECT & COPY IN MODAL" : "📋 COPY DAILY REPORT"}
                        </button>
                    </div>
                </div>
            </div>

            {/* HALTED BANNER — appears when Protective Liquidator has fired */}
            {live?.snapshot?.halted && (
                <div data-testid="halted-banner" style={{
                    background: "linear-gradient(90deg, rgba(239,68,68,0.18), rgba(239,68,68,0.06))",
                    border: `1.5px solid ${RED}`, borderRadius: 4, padding: "18px 22px",
                    marginBottom: 24, boxShadow: `0 0 32px rgba(239,68,68,0.25)`,
                }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                            <div style={{
                                width: 12, height: 12, borderRadius: "50%", background: RED,
                                boxShadow: `0 0 12px ${RED}`, animation: "pulse 1.4s infinite",
                            }} />
                            <div>
                                <div style={{ color: RED, fontSize: 11, letterSpacing: "0.3em", fontWeight: 700 }}>
                                    🛑 PROTECTIVE LIQUIDATOR TRIGGERED · BOT HALTED
                                </div>
                                <div style={{ color: "#fff", fontSize: 13, marginTop: 6, fontWeight: 500 }}>
                                    {live.snapshot.halt_reason || "Portfolio drawdown breached -5% hard cap. All positions liquidated to USDT."}
                                </div>
                                {live.snapshot.halted_at && (
                                    <div style={{ color: "#888", fontSize: 10, letterSpacing: "0.2em", marginTop: 4 }}>
                                        HALTED AT: {new Date(live.snapshot.halted_at).toISOString().replace("T", " ").slice(0, 19)} UTC
                                    </div>
                                )}
                            </div>
                        </div>
                        <button
                            data-testid="manual-resume-btn"
                            onClick={async () => {
                                const secret = window.prompt("Enter BOT_SECRET to force manual resume:");
                                if (!secret) return;
                                const notes = window.prompt("Optional notes (why are you resuming?):", "manual review — conditions look stable") || "";
                                try {
                                    const r = await fetch(`${BACKEND}/api/bot/resume`, {
                                        method: "POST",
                                        headers: { "Content-Type": "application/json", "X-Bot-Secret": secret },
                                        body: JSON.stringify({ notes }),
                                    });
                                    const d = await r.json();
                                    if (r.ok && d.ok) {
                                        window.alert("✓ Resume flag posted. Local bot will pick it up on next tick (≤60s) and rebuild grid at current prices.");
                                    } else {
                                        window.alert(`Resume failed: ${d.detail || r.status}`);
                                    }
                                } catch (e) { window.alert(`Network error: ${e.message}`); }
                            }}
                            style={{
                                background: RED, border: "none", color: "#fff", fontWeight: 700,
                                padding: "12px 24px", borderRadius: 3, cursor: "pointer",
                                fontSize: 11, letterSpacing: "0.2em",
                            }}
                        >⚡ MANUAL RESUME (OVERRIDE)</button>
                    </div>
                    <div style={{ color: "#888", fontSize: 11, marginTop: 12, borderTop: `1px solid rgba(239,68,68,0.2)`, paddingTop: 10, lineHeight: 1.6 }}>
                        <strong style={{ color: "#eab308" }}>Auto-resume</strong> is armed — bot will re-enter automatically when Wyckoff accumulation confirms (spring + LPS + volume dry-up).
                        <strong style={{ color: RED, marginLeft: 8 }}>Manual resume</strong> forces an immediate re-entry at current prices; use only after reviewing charts.
                    </div>
                </div>
            )}

            {/* LIVE FROM YOUR MAC — real-time data pushed by local bot */}
            {(() => {
                const firstFetchPending = live === null;  // React first-mount before any fetch resolves
                const isLive = live?.live && live?.snapshot;
                const snap = live?.snapshot || {};
                const gridEq = snap.grid_equity ?? snap.total_equity ?? 0;
                const earnAlloc = snap.earn_allocation ?? 0;
                const totalEq = snap.current_equity_total ?? (gridEq + earnAlloc);
                // P&L% = TRUE portfolio value (Grid + Earn) vs $10K Martyn LP live baseline.
                const MARTYN_BASELINE = 10000.00;
                const retPct = MARTYN_BASELINE > 0 ? ((totalEq - MARTYN_BASELINE) / MARTYN_BASELINE) * 100 : 0;
                const assets = snap.assets || {};
                const trades = live?.recent_trades || [];
                const lastAt = live?.snapshot?.received_at
                    ? new Date(live.snapshot.received_at)
                    : null;
                const secondsAgo = lastAt
                    ? Math.floor((Date.now() - lastAt.getTime()) / 1000)
                    : null;
                const stale = secondsAgo === null || secondsAgo > 180;
                // 3-state label: loading (initial fetch pending) → live (green) → stale (yellow)
                const statusLabel = firstFetchPending
                    ? "CONNECTING TO YOUR BOT…"
                    : (isLive && !stale ? "LIVE FROM YOUR MAC" : "WAITING FOR BOT DATA");
                const statusColor = firstFetchPending ? "#9ca3af" : (isLive && !stale ? GREEN : "#eab308");
                return (
                    <div data-testid="live-bot-section" style={{
                        background: isLive && !stale ? "rgba(34,197,94,0.06)" : (firstFetchPending ? "rgba(156,163,175,0.06)" : "rgba(234,179,8,0.06)"),
                        border: `1px solid ${isLive && !stale ? "rgba(34,197,94,0.35)" : (firstFetchPending ? "rgba(156,163,175,0.25)" : "rgba(234,179,8,0.35)")}`,
                        borderRadius: 4, padding: "16px 20px", marginBottom: 24,
                        transition: "background 400ms ease, border 400ms ease",
                    }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: isLive ? 12 : 0 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                <div style={{
                                    width: 8, height: 8, borderRadius: "50%",
                                    background: statusColor,
                                    boxShadow: isLive && !stale ? `0 0 8px ${GREEN}` : "none",
                                    animation: (isLive && !stale) || firstFetchPending ? "pulse 2s infinite" : "none",
                                    transition: "background 400ms ease",
                                }} />
                                <span style={{ color: statusColor, fontSize: 10, letterSpacing: "0.25em", fontWeight: 700 }}>
                                    {statusLabel}
                                </span>
                                {lastAt && (
                                    <span style={{ color: "#888", fontSize: 10, letterSpacing: "0.15em" }}>
                                        · updated {secondsAgo < 60 ? `${secondsAgo}s` : `${Math.floor(secondsAgo / 60)}m`} ago
                                    </span>
                                )}
                            </div>
                            {isLive && !stale && (() => {
                                return (
                                    <div style={{ display: "flex", gap: 20, alignItems: "baseline" }}>
                                        <div>
                                            <span style={{ color: "#888", fontSize: 10, letterSpacing: "0.2em", marginRight: 8 }}>EQUITY</span>
                                            <span style={{ color: "#fff", fontFamily: "'JetBrains Mono', 'SF Mono', 'Menlo', monospace", fontSize: 20, fontWeight: 600 }}>
                                                ${totalEq.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </span>
                                        </div>
                                        <div>
                                            <span style={{ color: "#888", fontSize: 10, letterSpacing: "0.2em", marginRight: 8 }}>P&L</span>
                                            <span style={{ color: flatColor(retPct), fontFamily: "'JetBrains Mono', 'SF Mono', 'Menlo', monospace", fontSize: 20, fontWeight: 600 }}>
                                                {retPct >= 0 ? "+" : ""}{retPct.toFixed(2)}%
                                            </span>
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>

                        {!isLive && (
                            <div style={{ color: "#888", fontSize: 12, marginTop: 8, lineHeight: 1.6 }}>
                                Your bot is running locally but hasn&apos;t synced yet. Run the <span style={{ color: GOLD, fontFamily: "monospace" }}>sync_dashboard.py</span> script alongside your bot to see live data here.
                            </div>
                        )}

                        {isLive && Object.keys(assets).length > 0 && (
                            <div style={{
                                display: "grid",
                                gridTemplateColumns: `repeat(${Math.min(Object.keys(assets).length, 3)}, 1fr)`,
                                gap: 12,
                            }}>
                                {Object.entries(assets).map(([sym, a]) => {
                                    // Format resume time for paused assets
                                    let resumeStr = "";
                                    if (a.is_paused && a.risk_state?.pause_until_ts) {
                                        const pu = a.risk_state.pause_until_ts;
                                        const puMs = pu > 1e12 ? pu : pu * 1000;
                                        const resumeDate = new Date(puMs);
                                        const diffMin = Math.round((puMs - Date.now()) / 60000);
                                        if (diffMin <= 0) {
                                            resumeStr = "resuming next tick";
                                        } else if (diffMin < 60) {
                                            resumeStr = `resumes in ${diffMin}m`;
                                        } else if (diffMin < 24 * 60) {
                                            const h = Math.floor(diffMin / 60);
                                            const m = diffMin % 60;
                                            resumeStr = `resumes in ${h}h ${m}m`;
                                        } else {
                                            resumeStr = `resumes ${resumeDate.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}`;
                                        }
                                    }
                                    return (
                                    <div key={sym} data-testid={`live-asset-${sym.split("/")[0]}`} style={{
                                        background: "rgba(0,0,0,0.3)", border: `1px solid ${BORDER}`,
                                        borderRadius: 3, padding: "10px 12px",
                                    }}>
                                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                                            <span style={{ color: GOLD, fontSize: 10, letterSpacing: "0.15em", fontWeight: 700 }}>{sym}</span>
                                            <span style={{ color: a.is_killed ? RED : a.is_paused ? "#eab308" : GREEN, fontSize: 8, letterSpacing: "0.15em", fontWeight: 700 }}>
                                                {a.is_killed ? "STOPPED" : a.is_paused ? "PAUSED" : "ACTIVE"}
                                            </span>
                                        </div>
                                        <div style={{ color: "#fff", fontFamily: "'JetBrains Mono', 'SF Mono', 'Menlo', monospace", fontSize: 16, fontWeight: 600 }}>
                                            ${(a.equity || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                                        </div>
                                        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4, fontSize: 10, color: "#888" }}>
                                            <span style={{ color: flatColor(a.return_pct || 0, 0.1) }}>{(a.return_pct || 0).toFixed(2)}%</span>
                                            <span title="Lifetime orders filled at exchange (permanent audit trail — never resets)">{(fills?.per_asset?.[sym]) ?? (a.trades_completed || 0)} fills</span>
                                            <span>@ ${(a.price || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
                                        </div>
                                        {a.is_paused && (
                                            <div data-testid={`pause-reason-${sym.split("/")[0]}`} style={{
                                                marginTop: 8, padding: "6px 8px",
                                                background: "rgba(234,179,8,0.08)",
                                                border: "1px solid rgba(234,179,8,0.3)",
                                                borderRadius: 2, fontSize: 9,
                                            }}>
                                                <div style={{ color: "#eab308", fontWeight: 600, letterSpacing: "0.03em" }}>
                                                    🛡 {a.risk_state?.pause_reason || "Auto-paused by risk manager"}
                                                </div>
                                                {resumeStr && (
                                                    <div style={{ color: "#a3a3a3", marginTop: 2 }}>
                                                        {resumeStr}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    );
                                })}
                            </div>
                        )}

                        {isLive && trades.length > 0 && (
                            <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${BORDER}` }}>
                                <div style={{ color: GOLD, fontSize: 9, letterSpacing: "0.25em", marginBottom: 4, fontWeight: 700 }}>RECENT ROUND-TRIPS ({trades.length})</div>
                                <div style={{ color: "#6b7280", fontSize: 10, marginBottom: 8, fontStyle: "italic" }}>
                                    Completed buy→sell cycles only. Single-leg grid fills tracked in per-asset Fills column below.
                                </div>
                                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                    {trades.slice(0, 5).map((t, i) => (
                                        <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr", fontSize: 10, color: "#aaa", padding: "3px 0" }}>
                                            <span style={{ color: "#fff" }}>{t.symbol}</span>
                                            <span>Buy ${(t.buy_price || 0).toFixed(2)}</span>
                                            <span>Sell ${(t.sell_price || 0).toFixed(2)}</span>
                                            <span>{(t.quantity || 0).toFixed(6)}</span>
                                            <span style={{ color: (t.pnl_net || 0) >= 0 ? GREEN : RED, textAlign: "right" }}>
                                                {(t.pnl_net || 0) >= 0 ? "+" : ""}${(t.pnl_net || 0).toFixed(2)}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {isLive && trades.length === 0 && Object.keys(assets).length > 0 && (() => {
                            const openTotal = Object.values(assets).reduce((s, a) => s + (a.open_grid_levels || 0), 0);
                            return (
                                <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${BORDER}`, color: "#888", fontSize: 11, lineHeight: 1.6 }} data-testid="grid-status-note">
                                    Grid strategy has <span style={{ color: GOLD }}>{openTotal}</span> open buy/sell orders armed across all assets. Realized PnL updates in the KPI card above whenever a full round-trip (buy → sell) closes.
                                </div>
                            );
                        })()}

                        {/* ═══ RESTART LOG — ZERO DATA LOSS TRACK RECORD ═══ */}
                        {(() => {
                            const totalRestarts = restarts?.total_restarts || 0;
                            const preservedCount = restarts?.state_preserved_count || 0;
                            const zeroLossPct = restarts?.zero_data_loss_pct ?? 100;
                            const recent = (restarts?.recent_restarts || []).slice(0, 5);
                            return (
                                <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${BORDER}` }} data-testid="restart-log-widget">
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 }}>
                                        <div style={{ color: GOLD, fontSize: 9, letterSpacing: "0.25em", fontWeight: 700 }}>
                                            🛡️ RESTART LOG — ZERO DATA LOSS ({totalRestarts})
                                        </div>
                                        <div style={{ color: totalRestarts > 0 && zeroLossPct === 100 ? GREEN : "#888", fontSize: 11, fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}>
                                            {totalRestarts > 0
                                                ? `${preservedCount}/${totalRestarts} preserved (${zeroLossPct}%)`
                                                : "no restarts yet"}
                                        </div>
                                    </div>
                                    {recent.length > 0 ? (
                                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                            {recent.map((r, i) => {
                                                const preserved = r.state_preserved;
                                                return (
                                                    <div key={i} data-testid={`restart-event-${i}`} style={{
                                                        display: "grid",
                                                        gridTemplateColumns: "140px 100px 130px 1fr",
                                                        alignItems: "center",
                                                        padding: "8px 12px",
                                                        background: "rgba(255,255,255,0.015)",
                                                        border: `1px solid ${BORDER}`,
                                                        borderLeft: `3px solid ${preserved ? GREEN : "#888"}`,
                                                        fontSize: 11,
                                                    }}>
                                                        <span style={{ color: "#888", fontFamily: "'JetBrains Mono', monospace", fontSize: 10 }}>
                                                            {(r.detected_at || "").replace("T", " ").slice(5, 19)}
                                                        </span>
                                                        <span style={{ color: "#aaa", fontFamily: "'JetBrains Mono', monospace" }}>
                                                            gap {r.gap_human || "?"}
                                                        </span>
                                                        <span style={{ color: preserved ? GREEN : GOLD, fontWeight: 700, fontSize: 10, letterSpacing: "0.1em" }}>
                                                            {preserved ? "✓ STATE PRESERVED" : "△ STATE RESET"}
                                                        </span>
                                                        <span style={{ color: "#666", fontSize: 10, fontFamily: "'JetBrains Mono', monospace", textAlign: "right" }}>
                                                            equity carried: ${(r.new_total_equity || 0).toFixed(2)}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div style={{ color: "#888", fontSize: 11, lineHeight: 1.6 }}>
                                            Bot has run continuously since first sync — no restart events logged yet. Every restart from here will appear above with the equity carried over, proving zero data loss across the roadmap.
                                        </div>
                                    )}
                                </div>
                            );
                        })()}
                    </div>
                );
            })()}

            {/* HERO CARD — the ONE metric that answers "am I up or down?" */}
            {(() => {
                const snap = live?.snapshot || {};
                const isLive = live?.live && live?.snapshot;
                if (!isLive) return null;
                // LP-GRADE COST-BASIS TRUTH:
                //   STARTED WITH  = inception_capital (original deposit, day-1)
                //   NET P&L       = current_equity − total_deposits (subtracts all capital injections)
                //   %             = lifetime_pnl_pct (returns on total invested capital)
                // Never resets. Baseline resets are FORBIDDEN. Capital events are ledgered separately.
                const inceptionCap = stats?.inception_capital ?? BOT_CONFIG.STARTING_CAPITAL;
                const totalDeposits = stats?.total_deposits ?? inceptionCap;
                const capitalEvents = stats?.capital_events || [];
                // Real total NAV = Grid equity + live Bybit Earn (L3 USDC + L4 stETH×ETH price)
                const gridEqLive = snap.grid_equity ?? snap.total_equity ?? 0;
                const earnEqLive = snap.earn_allocation ?? 0;
                const current = snap.current_equity_total ?? (gridEqLive + earnEqLive);
                // ═══ MARTYN LP LIVE IGNITION · 13 Aug 2026 · $10K TRANCHE 1 ═══
                const RESTART_BASELINE = 10000.00;
                const RESTART_DATE = "Aug 13, 2026";
                const pnl = current - RESTART_BASELINE;
                const pnlPct = RESTART_BASELINE > 0 ? (pnl / RESTART_BASELINE) * 100 : 0;
                // Lifetime deposit-vs-NAV kept as audit reference (not hero display).
                const lifetimeDelta = current - totalDeposits;
                const startCap = totalDeposits;
                const isUp = pnl >= 0;
                const color = isUp ? GREEN : "#ef4444";
                const bg = isUp ? "rgba(34,197,94,0.08)" : "rgba(239,68,68,0.08)";
                const border = isUp ? "rgba(34,197,94,0.35)" : "rgba(239,68,68,0.35)";

                // Realized = TRUE NAV vs $10K baseline (Grid + Earn already included).
                const realized = current - RESTART_BASELINE;
                const unrealized = 0; // live mode: NAV = everything (backend authoritative)

                // ═══ TODAY'S REALIZED · 24h delta since Martyn LP live ignition ═══
                const REALIZED_24H_BASELINE = 10000.00;  // must match RESTART_BASELINE
                const rawTodayRealized = snap.realized_24h ?? 0;
                const currentEqFor24h = current;
                const raw24hFloored = Math.max(rawTodayRealized, currentEqFor24h - REALIZED_24H_BASELINE);
                const todayRealized = raw24hFloored;
                const todayCount = snap.fills_24h ?? 0;

                return (
                    <div data-testid="hero-money-card" style={{
                        background: bg, border: `1px solid ${border}`, borderRadius: 16,
                        padding: "28px 32px", marginBottom: 24,
                    }}>
                        <div style={{ fontSize: 11, letterSpacing: 2, color: "#9ca3af", marginBottom: 12, fontWeight: 700 }}>
                            YOUR MONEY
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1.4fr", gap: 32, alignItems: "end" }}>
                            <div>
                                <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 4 }}>STARTED WITH</div>
                                <div style={{ fontSize: 28, fontWeight: 700, color: "#fff" }} data-testid="hero-inception-capital">
                                    ${inceptionCap.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </div>
                                {capitalEvents.length > 0 && (
                                    <div style={{ fontSize: 9.5, color: "#9ca3af", marginTop: 6, lineHeight: 1.5 }} data-testid="capital-events-note">
                                        {capitalEvents.map((e, i) => (
                                            <div key={i} style={{ marginBottom: 2 }}>
                                                <span style={{ color: e.amount >= 0 ? "#22c55e" : "#f59e0b" }}>
                                                    {e.amount >= 0 ? "+" : "−"} ${Math.abs(e.amount).toLocaleString()}
                                                </span>
                                                {" "}{e.amount >= 0 ? "added" : "reallocated"} {e.date ? new Date(e.date).toISOString().slice(0, 10) : ""}
                                                {e.reason && (
                                                    <div style={{ color: "#6b7280", fontSize: 9, marginLeft: 8, fontStyle: "italic" }}>
                                                        ↳ {e.reason}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                        <div style={{ color: "#eab308", marginTop: 5, fontWeight: 600 }}>
                                            Total invested: ${totalDeposits.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div>
                                <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 4 }}>NOW</div>
                                <div style={{ fontSize: 28, fontWeight: 700, color: "#fff" }} data-testid="hero-current-equity">
                                    ${current.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </div>
                            </div>
                            <div>
                                <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 4 }}>NET P&L · SINCE MARTYN LP LIVE IGNITION ({RESTART_DATE})</div>
                                <div style={{ fontSize: 40, fontWeight: 800, color, lineHeight: 1 }} data-testid="hero-pnl">
                                    {isUp ? "+" : "−"}${Math.abs(pnl).toFixed(2)}
                                    <span style={{ fontSize: 20, marginLeft: 12, opacity: 0.85 }}>
                                        ({isUp ? "+" : ""}{pnlPct.toFixed(2)}%)
                                    </span>
                                </div>
                                <div style={{ fontSize: 9.5, color: "#9ca3af", marginTop: 6 }}>
                                    Bybit UTA · LIVE mode · Martyn LP tranche 1 · $10K ignition
                                </div>
                                <div style={{ fontSize: 8.5, color: "#6b7280", marginTop: 3, lineHeight: 1.5 }}>
                                    <span>Prior paper-era baselines retired · anchored to 13 Aug 2026 live flip</span>
                                </div>
                            </div>
                        </div>

                        {/* WITHDRAW CARD — full sweep model: take everything above baseline (Rule Zero) */}
                        {isLive && (() => {
                            const banked = snap.banked_profit ?? 0;
                            const bankedCount = snap.banked_trades_count ?? 0;
                            // ═══ SHAVEABLE BASELINE · MATCHES BACKEND GRID_USDT_BASELINE ═══
                            // Grid seed at 13 Aug 2026 ignition: $8,520 (excl. L3+L4 Earn).
                            // Split 50/50: $4,260 USDT quote + $4,260 pre-bought base.
                            // Backend banked_profit = cash_pool - $4,260 (balance-based, real).
                            // Any USDT above $4,260 = grown from grid sells since ignition.
                            const SHAVE_BASELINE_CASH = 4260.00;
                            const cashSurplus = (snap.cash_pool ?? 0) - SHAVE_BASELINE_CASH;
                            const fa = snap.funding_arb || {};
                            // Use funding_pnl only (net_pnl of closed Layer 2 trades — real cash).
                            // total_funding_received includes open positions' accrued funding (unrealized),
                            // so adding both = double-count. funding_pnl alone = LP-defensible.
                            const fundingEarned = Number(fa.funding_pnl) || 0;
                            // Banked is a PROTECTED slice inside cash_pool (bot won't touch for rebalancing).
                            // Unpaired = cash surplus above banked (real "cash from open sells").
                            const unpairedCash = Math.max(0, cashSurplus - banked);
                            // PRD FORMULA (line 314): Sweep = Locked profit + Cash from open sells + Funding
                            const rawSweepable = banked + unpairedCash + fundingEarned;
                            // Subtract lifetime already-swept so we don't count it twice
                            const totalSweptLifetime = sweepData?.total_swept_lifetime || 0;
                            const totalShaveable = rawSweepable - totalSweptLifetime;
                            return (
                                <div style={{
                                    marginTop: 20,
                                    padding: "24px 28px",
                                    background: "linear-gradient(135deg, rgba(34,197,94,0.18), rgba(16,185,129,0.06))",
                                    border: "1.5px solid rgba(34,197,94,0.4)",
                                    borderRadius: 12,
                                }} data-testid="shave-band">
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 20, flexWrap: "wrap" }}>
                                      <div style={{ flex: 1, minWidth: 280 }}>
                                        <div style={{ fontSize: 11, color: "#86efac", letterSpacing: 2, fontWeight: 700, marginBottom: 6 }}>
                                            REALIZED AVAILABLE · WITHDRAWABLE
                                        </div>
                                        <div style={{ fontSize: 48, fontWeight: 800, color: totalShaveable >= 0 ? "#22c55e" : "#ef4444", lineHeight: 1, fontFamily: "'Playfair Display', serif", marginBottom: 6 }} data-testid="total-shaveable">
                                            {totalShaveable >= 0 ? "+" : "−"}${Math.abs(totalShaveable).toFixed(2)}
                                        </div>
                                        <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 14, lineHeight: 1.6 }}>
                                            Realized profits above ${totalDeposits.toLocaleString()} working capital · trading loop unaffected · capped at closed-trade earnings
                                            {totalSweptLifetime > 0 && (
                                                <span style={{ color: "#60a5fa", marginLeft: 8 }}>
                                                    · Lifetime distributed: ${totalSweptLifetime.toFixed(2)}
                                                </span>
                                            )}
                                        </div>
                                      </div>
                                      <button
                                          onClick={() => setSweepModal({ open: true, prefillAmount: totalShaveable.toFixed(2) })}
                                          disabled={totalShaveable <= 0}
                                          data-testid="record-sweep-btn"
                                          style={{
                                              padding: "12px 20px", borderRadius: 8,
                                              background: totalShaveable > 0 ? "#22c55e" : "#374151",
                                              color: "#fff", fontWeight: 700, letterSpacing: 1,
                                              border: "none", cursor: totalShaveable > 0 ? "pointer" : "not-allowed",
                                              fontSize: 13, whiteSpace: "nowrap",
                                          }}
                                      >
                                          RECORD DISTRIBUTION →
                                      </button>
                                    </div>
                                    <div style={{ display: "flex", gap: 28, fontSize: 13, color: "#d1d5db", flexWrap: "wrap" }}>
                                        <div data-testid="shave-locked-profit">
                                            <span style={{ color: "#9ca3af" }}>Locked profit: </span>
                                            <span style={{ color: "#22c55e", fontWeight: 700 }}>${banked.toFixed(2)}</span>
                                            <span style={{ color: "#6b7280", marginLeft: 4, fontSize: 11 }}>({bankedCount} closed trades · audit-clean)</span>
                                        </div>
                                        <div data-testid="shave-unpaired">
                                            <span style={{ color: "#9ca3af" }}>Cash from open sells: </span>
                                            <span style={{ color: unpairedCash > 0 ? "#22c55e" : "#9ca3af", fontWeight: 700 }}>${unpairedCash.toFixed(2)}</span>
                                        </div>
                                        <div data-testid="shave-funding">
                                            <span style={{ color: "#9ca3af" }}>Funding arbitrage P&L: </span>
                                            <span style={{ color: fundingEarned > 0 ? "#22c55e" : (fundingEarned < 0 ? "#f59e0b" : "#9ca3af"), fontWeight: 700 }}>{fundingEarned >= 0 ? "+" : "−"}${Math.abs(fundingEarned).toFixed(2)}</span>
                                            <span style={{ color: "#6b7280", marginLeft: 4, fontSize: 11 }}>(Layer 2 · live since {fa.fresh_start_date || "29 Jul 2026"} · 12% APY entry threshold)</span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}
                        {/* ═══ LP-HONEST PROFIT SPLIT · Withdrawable vs Locked-in ═══
                            Exposes the truth: "banked" is a floating snapshot, not locked profit.
                            Withdrawable = free USDT above $4,260 baseline (shrinks on BUY dips, grows on SELLs)
                            Locked-in    = grid P&L already compounded into base holdings via BUY fills */}
                        {isLive && (() => {
                            const withdrawable = snap.withdrawable_now ?? 0;
                            const lockedIn = snap.locked_in_growth ?? 0;
                            const gridReturn = snap.grid_return_total ?? 0;
                            const isUnderwater = gridReturn < 0;
                            return (
                                <div data-testid="profit-composition" style={{
                                    marginTop: 14, padding: "18px 22px",
                                    background: "rgba(255,255,255,0.02)",
                                    border: "1px solid rgba(255,255,255,0.08)",
                                    borderRadius: 10,
                                }}>
                                    <div style={{ fontSize: 10, color: "#9ca3af", letterSpacing: 2, fontWeight: 700, marginBottom: 14 }}>
                                        PROFIT COMPOSITION · LP HONEST VIEW
                                    </div>
                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                                        <div data-testid="withdrawable-now-card" style={{
                                            padding: "14px 16px", borderRadius: 8,
                                            background: "linear-gradient(135deg, rgba(34,197,94,0.10), rgba(16,185,129,0.02))",
                                            border: "1px solid rgba(34,197,94,0.25)",
                                        }}>
                                            <div style={{ fontSize: 9.5, color: "#86efac", letterSpacing: 1.5, fontWeight: 700, marginBottom: 6 }}>
                                                SAFE TO WITHDRAW
                                            </div>
                                            <div style={{ fontSize: 28, fontWeight: 800, color: "#22c55e", fontFamily: "'Playfair Display', serif", lineHeight: 1, marginBottom: 8 }}>
                                                ${(snap.safe_to_withdraw ?? withdrawable).toFixed(2)}
                                            </div>
                                            <div style={{ fontSize: 10.5, color: "#9ca3af", lineHeight: 1.55 }}>
                                                This is what Martyn can sweep right now. Zero grid disruption.
                                            </div>
                                        </div>
                                        <div data-testid="locked-in-growth-card" style={{
                                            padding: "14px 16px", borderRadius: 8,
                                            background: "linear-gradient(135deg, rgba(59,130,246,0.10), rgba(37,99,235,0.02))",
                                            border: "1px solid rgba(59,130,246,0.25)",
                                        }}>
                                            <div style={{ fontSize: 9.5, color: "#93c5fd", letterSpacing: 1.5, fontWeight: 700, marginBottom: 6 }}>
                                                LOCKED-IN GROWTH · COMPOUNDED
                                            </div>
                                            <div style={{ fontSize: 28, fontWeight: 800, color: isUnderwater ? "#ef4444" : "#3b82f6", fontFamily: "'Playfair Display', serif", lineHeight: 1, marginBottom: 8 }}>
                                                {isUnderwater ? "−" : ""}${Math.abs(isUnderwater ? gridReturn : lockedIn).toFixed(2)}
                                            </div>
                                            <div style={{ fontSize: 10.5, color: "#9ca3af", lineHeight: 1.55 }}>
                                                {isUnderwater
                                                    ? <>Grid currently underwater by ${Math.abs(gridReturn).toFixed(2)} unrealized. Waiting on next SELL cycle to convert base holdings back to USDT.</>
                                                    : <>Grid P&L already compounded into base holdings via BUY fills. Realizing = market-selling base coins · <span style={{ color: "#f59e0b" }}>disrupts the grid or crystallizes MTM</span>.</>
                                                }
                                            </div>
                                        </div>
                                    </div>
                                    <div style={{ fontSize: 10, color: "#6b7280", marginTop: 12, lineHeight: 1.5 }}>
                                        Grid total P&L: <span style={{ color: gridReturn >= 0 ? "#22c55e" : "#ef4444", fontWeight: 700 }}>{gridReturn >= 0 ? "+" : "−"}${Math.abs(gridReturn).toFixed(2)}</span>
                                        {" "}vs $8,520 tranche baseline · excludes Earn L3+L4 · updates every tick
                                    </div>
                                </div>
                            );
                        })()}
                        {/* SWEEP HISTORY TABLE — LP audit trail */}
                        {isLive && sweepData && sweepData.count > 0 && (
                            <div style={{
                                marginTop: 18, padding: "18px 22px",
                                background: "rgba(255,255,255,0.03)",
                                border: "1px solid rgba(255,255,255,0.08)",
                                borderRadius: 10,
                            }} data-testid="sweep-history">
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 12 }}>
                                    <div style={{ fontSize: 11, color: "#60a5fa", letterSpacing: 2, fontWeight: 700 }}>
                                        SWEEP HISTORY · {sweepData.count} EVENT{sweepData.count === 1 ? "" : "S"}
                                    </div>
                                    <div style={{ fontSize: 13, color: "#22c55e", fontWeight: 700 }}>
                                        Lifetime Swept: ${sweepData.total_swept_lifetime.toFixed(2)}
                                    </div>
                                </div>
                                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                                    <thead>
                                        <tr style={{ color: "#9ca3af", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                                            <th style={{ textAlign: "left", padding: "6px 8px", fontWeight: 600, letterSpacing: 1 }}>DATE</th>
                                            <th style={{ textAlign: "right", padding: "6px 8px", fontWeight: 600, letterSpacing: 1 }}>AMOUNT</th>
                                            <th style={{ textAlign: "left", padding: "6px 8px", fontWeight: 600, letterSpacing: 1 }}>TO</th>
                                            <th style={{ textAlign: "left", padding: "6px 8px", fontWeight: 600, letterSpacing: 1 }}>NOTE</th>
                                            <th style={{ textAlign: "left", padding: "6px 8px", fontWeight: 600, letterSpacing: 1 }}>MODE</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sweepData.sweeps.slice(0, 10).map((s) => (
                                            <tr key={s.id} style={{ borderBottom: "1px solid rgba(255,255,255,0.03)" }}>
                                                <td style={{ padding: "6px 8px", color: "#d1d5db" }}>{new Date(s.swept_at).toLocaleDateString()} {new Date(s.swept_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</td>
                                                <td style={{ padding: "6px 8px", color: "#22c55e", fontWeight: 700, textAlign: "right", fontFamily: "'Playfair Display', serif" }}>${s.amount.toFixed(2)}</td>
                                                <td style={{ padding: "6px 8px", color: "#fff" }}>{s.destination}</td>
                                                <td style={{ padding: "6px 8px", color: "#9ca3af", fontStyle: "italic", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis" }}>{s.note || "—"}</td>
                                                <td style={{ padding: "6px 8px", color: s.mode === "live" ? "#22c55e" : "#f59e0b", fontSize: 10, letterSpacing: 1 }}>{s.mode.toUpperCase()}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {sweepData.count > 10 && (
                                    <div style={{ fontSize: 10, color: "#6b7280", textAlign: "center", marginTop: 8, fontStyle: "italic" }}>Showing latest 10 of {sweepData.count}</div>
                                )}
                                {Object.keys(sweepData.by_destination).length > 1 && (
                                    <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid rgba(255,255,255,0.05)", fontSize: 11, color: "#9ca3af" }}>
                                        By destination:{" "}
                                        {Object.entries(sweepData.by_destination).map(([k, v]) => (
                                            <span key={k} style={{ marginRight: 14 }}>
                                                <span style={{ color: "#fff" }}>{k}:</span>{" "}
                                                <span style={{ color: "#22c55e", fontWeight: 700 }}>${v.toFixed(2)}</span>
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* LAYER STATUS CARD — 6-layer multi-strategy stack */}
                        {isLive && layerData && layerData.layers && (
                            <div style={{
                                marginTop: 18, padding: "20px 22px",
                                background: "linear-gradient(135deg, rgba(96,165,250,0.06), rgba(59,130,246,0.02))",
                                border: "1px solid rgba(96,165,250,0.25)", borderRadius: 10,
                            }} data-testid="layer-status-card">
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
                                    <div>
                                        <div style={{ fontSize: 11, color: "#60a5fa", letterSpacing: 2, fontWeight: 700 }}>
                                            STRATEGY STACK · {layerData.layers_live_count} LIVE · {layerData.layers_dormant_count} PENDING
                                        </div>
                                        <div style={{ fontSize: 10, color: "#6b7280", marginTop: 3, fontStyle: "italic" }}>
                                            Institutional multi-strategy · anchored to 13 Aug 2026 Martyn LP live ignition
                                        </div>
                                    </div>
                                    <div style={{ fontSize: 13, color: layerData.balance_status === "OK" ? "#22c55e" : "#f59e0b", fontWeight: 700 }}>
                                        {layerData.balance_status === "OK"
                                            ? `Bal on-exchange OK · below $${layerData.max_exchange_balance_cap.toLocaleString()} cap`
                                            : `⚠ OVER CAP by $${layerData.balance_over_cap_usd.toFixed(2)} · SWEEP`}
                                    </div>
                                </div>
                                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 10 }}>
                                    {layerData.layers.map((L) => {
                                        const healthColor = {
                                            live: "#22c55e", paper_sim: "#60a5fa",
                                            dormant: "#f59e0b", cold: "#f59e0b",
                                            halted: "#ef4444", not_built: "#6b7280",
                                        }[L.health] || "#9ca3af";
                                        return (
                                            <div key={L.id} data-testid={`layer-${L.id}`} style={{
                                                padding: "12px 14px",
                                                background: "rgba(255,255,255,0.03)",
                                                border: `1px solid ${healthColor}44`,
                                                borderLeft: `3px solid ${healthColor}`,
                                                borderRadius: 6,
                                            }}>
                                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
                                                    <div style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>
                                                        L{L.id} · {L.name}
                                                    </div>
                                                    <div style={{ fontSize: 9, color: healthColor, letterSpacing: 1.5, fontWeight: 700 }}>
                                                        {L.health.replace("_", " ").toUpperCase()}
                                                    </div>
                                                </div>
                                                <div style={{ fontSize: 10, color: "#9ca3af", lineHeight: 1.4, marginBottom: 6 }}>
                                                    {L.description}
                                                </div>
                                                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11 }}>
                                                    <div>
                                                        <span style={{ color: "#6b7280" }}>Alloc: </span>
                                                        <span style={{ color: "#d1d5db", fontWeight: 700 }}>${L.allocation_usd.toFixed(0)}</span>
                                                        <span style={{ color: "#6b7280" }}> ({L.allocation_pct}%)</span>
                                                    </div>
                                                    <div>
                                                        <span style={{ color: "#6b7280" }}>PnL: </span>
                                                        <span style={{ color: (L.pnl_usd || 0) > 0 ? "#22c55e" : (L.pnl_usd < 0 ? "#f59e0b" : "#9ca3af"), fontWeight: 700 }}>
                                                            {(L.pnl_usd || 0) >= 0 ? "+" : "−"}${Math.abs(L.pnl_usd || 0).toFixed(2)}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div style={{ fontSize: 9, color: "#6b7280", marginTop: 3, fontStyle: "italic" }}>
                                                    {L.metric}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid rgba(96,165,250,0.15)", fontSize: 11, color: "#9ca3af", display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
                                    <div>Total allocation: <span style={{ color: "#fff", fontWeight: 700 }}>{layerData.total_allocation_pct}%</span> of ${layerData.restart_baseline.toLocaleString()} baseline</div>
                                    <div>Combined paper PnL: <span style={{ color: layerData.total_pnl_paper > 0 ? "#22c55e" : "#f59e0b", fontWeight: 700 }}>${layerData.total_pnl_paper.toFixed(2)}</span></div>
                                </div>
                            </div>
                        )}
                        {(realized !== 0 || unrealized !== 0) && (
                            <div style={{ marginTop: 20, paddingTop: 16, borderTop: "1px solid rgba(255,255,255,0.08)",
                                display: "flex", gap: 32, fontSize: 13, color: "#d1d5db", flexWrap: "wrap" }}>
                                <div data-testid="hero-today-realized">
                                    <span style={{ color: "#9ca3af" }}>Today (last 24h): </span>
                                    <span style={{ color: todayRealized >= 0 ? GREEN : "#ef4444", fontWeight: 700, fontSize: 15 }}>
                                        {todayRealized >= 0 ? "+" : "−"}${Math.abs(todayRealized).toFixed(2)}
                                    </span>
                                    <span style={{ color: "#6b7280", marginLeft: 6, fontSize: 11 }}>
                                        ({todayCount} {todayCount === 1 ? "trade" : "trades"} closed)
                                    </span>
                                </div>
                                <div>
                                    <span style={{ color: "#9ca3af" }}>NAV change (volatile): </span>
                                    <span style={{ color: realized >= 0 ? GREEN : "#ef4444", fontWeight: 600 }}>
                                        {realized >= 0 ? "+" : "−"}${Math.abs(realized).toFixed(2)}
                                    </span>
                                    <span style={{ color: "#6b7280", marginLeft: 6, fontSize: 11 }}>
                                        (fluctuates with market · not withdrawable)
                                    </span>
                                </div>
                                <div>
                                    <span style={{ color: "#9ca3af" }}>Unrealized (open): </span>
                                    <span style={{ color: unrealized >= 0 ? GREEN : "#ef4444", fontWeight: 600 }}>
                                        {unrealized >= 0 ? "+" : "−"}${Math.abs(unrealized).toFixed(2)}
                                    </span>
                                    <span style={{ color: "#6b7280", marginLeft: 6, fontSize: 11 }}>
                                        (live mode · NAV = everything)
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                );
            })()}

            {/* TOP KPI ROW — LIVE from your Mac bot (falls back to backtest if offline) */}
            {(() => {
                const isLive = live?.live && live?.snapshot;
                const snap = live?.snapshot || {};
                const assetsMap = snap.assets || {};
                const roundTrips = Object.values(assetsMap).reduce((s, a) => s + (a.trades_completed || 0), 0);
                const openLevels = Object.values(assetsMap).reduce((s, a) => s + (a.open_grid_levels || 0), 0);
                // Starting grid levels = 20 per asset (locked in config)
                // Fills = starting - current open. Includes both single-leg fills AND completed round-trips.
                const startingLevelsPerAsset = BOT_CONFIG.STARTING_GRID_LEVELS;
                const assetCount = Object.keys(assetsMap).length;
                const totalStartingLevels = assetCount * startingLevelsPerAsset;
                const ordersFilled = Math.max(0, totalStartingLevels - openLevels);
                const liveEquity = snap.total_equity;
                // Portfolio Value KPI trend arrow: use deposit-adjusted lifetime return, not bot's local baseline.
                const liveReturn = (stats?.lifetime_pnl_pct !== undefined && stats?.lifetime_pnl_pct !== null)
                    ? stats.lifetime_pnl_pct
                    : snap.total_return_pct;
                // COST BASIS = sum of all deposits (inception + injections). Truth for allocation math.
                const startCap = stats?.total_deposits ?? (stats?.inception_capital ?? BOT_CONFIG.STARTING_CAPITAL);
                // Banked USDT = current USDT across all assets - starting USDT allocation ($4K = half of $8K)
                // Only moves when sells fire (USDT ↑) or buys fire (USDT ↓). Ignores crypto price fluctuations.
                const totalCurrentUsdt = Object.values(assetsMap).reduce((s, a) => s + (a.quote_holdings || 0), 0);
                const startingUsdt = startCap / 2;
                const bankedUsdt = totalCurrentUsdt - startingUsdt;

                // ═══ MARTYN LP LIVE · 13 Aug 2026 · $10K TRANCHE 1 ═══
                // Same reframe as hero card. Overrides snap.realized_pnl for the KPI row.
                // Uses TRUE portfolio value (Grid + Earn) so KPIs match hero card exactly.
                const KPI_RESTART_BASELINE = 10000.00;
                const kpiGridEq = snap.grid_equity ?? liveEquity ?? 0;
                const kpiEarn = snap.earn_allocation ?? 0;
                const kpiTotalEq = snap.current_equity_total ?? (kpiGridEq + kpiEarn);
                const reframedPnl = kpiTotalEq - KPI_RESTART_BASELINE;
                const reframedPnlPct = KPI_RESTART_BASELINE > 0 ? (reframedPnl / KPI_RESTART_BASELINE) * 100 : 0;

                return (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 12, marginBottom: 24 }}>
                        <KpiCard icon={DollarSign} label={isLive ? "Portfolio Value · LIVE" : "Portfolio Value"}
                            value={isLive ? `$${kpiTotalEq.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—"}
                            sub={isLive ? `from $${KPI_RESTART_BASELINE.toLocaleString()} Martyn LP baseline` : "waiting for bot data"}
                            trend={isLive ? reframedPnlPct : undefined}
                            color={isLive ? flatColor(reframedPnlPct) : "#666"}
                            testid="kpi-portfolio-value" />
                        <KpiCard icon={TrendingUp} label={isLive ? "Realized Yield" : "Yield"}
                            value={isLive ? (() => {
                                // REFRAMED: since 13 Aug Martyn LP live ignition.
                                const sign = reframedPnl >= 0 ? "+" : "−";
                                return `${sign}$${Math.abs(reframedPnl).toFixed(2)}`;
                            })() : "—"}
                            sub={isLive ? (() => {
                                return `${reframedPnlPct >= 0 ? "+" : ""}${reframedPnlPct.toFixed(2)}% · SINCE 13 AUG`;
                            })() : "—"}
                            color={isLive ? (reframedPnl > 0 ? GREEN : (reframedPnl < 0 ? RED : "#fff")) : "#666"}
                            testid="kpi-realized-yield" />
                        <KpiCard icon={BarChart3} label="Net Equity Growth"
                            value={isLive ? (() => {
                                return reframedPnl > 0 ? `+$${reframedPnl.toFixed(2)}` : (reframedPnl < 0 ? `−$${Math.abs(reframedPnl).toFixed(2)}` : "$0.00");
                            })() : "—"}
                            sub={isLive ? "since Martyn LP live · 13 Aug" : "—"}
                            color={isLive ? (reframedPnl > 0 ? GREEN : (reframedPnl < 0 ? RED : "#fff")) : "#666"}
                            testid="kpi-net-equity-growth" />
                        <KpiCard icon={DollarSign} label="USDT Position"
                            value={isLive ? (bankedUsdt > 0 ? `+$${bankedUsdt.toFixed(2)}` : (bankedUsdt < 0 ? `−$${Math.abs(bankedUsdt).toFixed(2)}` : "$0.00")) : "—"}
                            sub={isLive ? (() => {
                                if (bankedUsdt > 20) return "net seller · cash accumulating";
                                if (bankedUsdt < -20) return "net buyer · accumulating crypto cheap";
                                return "balanced · buys ≈ sells";
                            })() : "—"}
                            color={isLive ? (bankedUsdt > 0 ? GREEN : (bankedUsdt < 0 ? "#eab308" : "#fff")) : "#666"}
                            testid="kpi-usdt-position" />
                        <KpiCard icon={Activity} label="Open Grid Orders"
                            value={isLive ? openLevels.toString() : "—"}
                            sub={isLive ? "buy/sell levels armed" : "—"}
                            color={isLive ? GOLD : "#666"}
                            testid="kpi-open-orders" />
                    </div>
                );
            })()}

            {/* INSTITUTIONAL METRICS ROW — for Guild Capital review */}
            {(() => {
                const s = stats?.ready ? stats : null;
                // Sharpe honest math:
                //   sharpe_samples_have = post-reset daily returns (0 today)
                //   days_tracked        = lifetime (pre-reset legacy + post-reset)
                //   Sharpe formula needs 14 continuous daily returns → gated on post-reset n.
                //   Legacy pre-reset days are preserved as summary (win_rate, max_dd) but
                //   individual daily returns were lost in the 15 Jul snapshot purge.
                const postResetN = s?.sharpe_samples_have || 0;
                const lifetimeDays = s?.days_tracked || 0;
                const daysToUnlock = Math.max(0, (s?.sharpe_min_samples || 14) - postResetN);

                // CAPITAL-EVENT-ADJUSTED MAX DRAWDOWN & AVG DAILY RETURN.
                // Server's max_drawdown_pct treats capital deposits/reallocations as market moves.
                // Adjust: subtract the % impact of net capital events since inception from raw DD.
                // A -$790 reallocation on $9720 peak = -8.13% artificial DD; strip it out.
                const rawMaxDD = s?.max_drawdown_pct ?? 0;
                const rawAvgDaily = s?.avg_daily_return_pct ?? 0;
                const netCapEvents = (s?.capital_events || []).reduce((a, e) => a + (e.amount || 0), 0);
                const peakForAdj = s?.peak_equity || s?.current_equity || 1;
                const capImpactPct = peakForAdj > 0 ? (netCapEvents / peakForAdj) * 100 : 0;
                // CAPITAL-EVENT-ADJUSTED MAX DRAWDOWN — Martyn LP live era (13 Aug 2026+).
                // Show honest post-ignition DD from backend, adjusted only for real capital events.
                // No legacy floor. Pre-ignition paper-era peaks retired.
                const adjMaxDD = Math.min(0, rawMaxDD + Math.abs(capImpactPct));
                const finalMaxDD = adjMaxDD;

                const cards = [
                    {
                        icon: Activity, label: "Annualized Sharpe",
                        value: (s && s.sharpe_ready && s.annualized_sharpe !== null) ? s.annualized_sharpe.toFixed(2) : "—",
                        sub: s ? (s.sharpe_ready
                            ? (postResetN < 90
                                ? `${postResetN}d preliminary · 90d for LP-grade`
                                : `${lifetimeDays} lifetime days`)
                            : `${lifetimeDays} lifetime days · ${daysToUnlock}d to unlock`) : "need 14+ post-reset days",
                        color: (s && s.sharpe_ready && s.annualized_sharpe > 1) ? GREEN : "#fff",
                    },
                    {
                        icon: Shield, label: "Max Drawdown",
                        value: s ? `${finalMaxDD.toFixed(2)}%` : "—",
                        sub: s ? "vs $10K LP baseline · kill @ -5%" : "—",
                        color: s ? flatColor(-Math.abs(finalMaxDD), 5) : "#666",
                    },
                    {
                        icon: TrendingUp, label: "Win Rate (days)",
                        value: s ? `${s.win_rate_pct.toFixed(1)}%` : "—",
                        sub: s ? "% of green days" : "—",
                        color: s && s.win_rate_pct > 55 ? GREEN : "#fff",
                    },
                    {
                        icon: BarChart3, label: "Avg Daily Return",
                        value: (() => {
                            if (!s) return "—";
                            // MARTYN LP LIVE IGNITION ANCHOR · 13 Aug 2026 · $10,000 institutional Day-1.
                            const RESTART_BL = 10000.00;
                            const eq = s.current_equity || RESTART_BL;
                            const daysPost = s.days_tracked_post_reset || 1;
                            const pctSinceRestart = ((eq - RESTART_BL) / RESTART_BL) * 100;
                            const avgDailyPost = daysPost > 0 ? pctSinceRestart / daysPost : 0;
                            return `${avgDailyPost >= 0 ? "+" : ""}${avgDailyPost.toFixed(3)}%`;
                        })(),
                        sub: s ? `since 13 Aug Martyn LP live · ${s.days_tracked_post_reset || 0}d` : "—",
                        color: (() => {
                            if (!s) return "#666";
                            const RESTART_BL = 10000.00;
                            const eq = s.current_equity || RESTART_BL;
                            const daysPost = s.days_tracked_post_reset || 1;
                            const avgDaily = daysPost > 0 ? (((eq - RESTART_BL) / RESTART_BL) * 100) / daysPost : 0;
                            return flatColor(avgDaily, 0.05);
                        })(),
                    },
                ];
                return (
                    <div style={{ marginBottom: 24 }}>
                        <div style={{ color: GOLD, fontSize: 9, letterSpacing: "0.3em", marginBottom: 10, fontWeight: 700, textTransform: "uppercase" }}>
                            Institutional Metrics
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
                            {cards.map((c, i) => (
                                <Card key={i} data-testid={`inst-${c.label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                                        <c.icon size={16} color={GOLD} />
                                    </div>
                                    <Kicker>{c.label}</Kicker>
                                    <div style={{ fontFamily: "'JetBrains Mono', 'SF Mono', 'Menlo', monospace", fontSize: 22, fontWeight: 700, color: c.color, lineHeight: 1 }}>
                                        {c.value}
                                    </div>
                                    <div style={{ color: "#888", fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", marginTop: 6 }}>
                                        {c.sub}
                                    </div>
                                </Card>
                            ))}
                        </div>
                    </div>
                );
            })()}

            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16, marginBottom: 24 }}>

                {/* LIVE EQUITY CURVE — real bot data streaming */}
                <Card glow style={{ padding: "20px 20px 12px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                        <div>
                            <Kicker>Live Equity Curve · Your Bot</Kicker>
                            <h2 style={{ fontFamily: "Playfair Display, serif", color: "#fff", fontSize: 20, fontWeight: 400, margin: 0 }}>
                                Real-Time Portfolio Value
                            </h2>
                            <div style={{ color: "#666", fontSize: 10, marginTop: 4 }}>
                                {(() => {
                                    if (liveCurve.length === 0) return "Waiting for bot data...";
                                    const first = liveCurve[0]?.t;
                                    const last = liveCurve[liveCurve.length - 1]?.t;
                                    if (!first || !last) return `${liveCurve.length} snapshots`;
                                    const firstT = new Date(first);
                                    const lastT = new Date(last);
                                    const spanH = Math.round((lastT - firstT) / 3600000 * 10) / 10;
                                    const rangeH = historyRange;
                                    if (spanH < rangeH * 0.95) {
                                        return `${liveCurve.length} snapshots · ${spanH}h of data (bot age) · showing all since launch`;
                                    }
                                    return `${liveCurve.length} snapshots · ${spanH}h window · updating every 30s`;
                                })()}
                            </div>
                        </div>
                        <div style={{ display: "flex", gap: 6 }}>
                            {[{ h: 1, l: "1H" }, { h: 6, l: "6H" }, { h: 24, l: "1D" }, { h: 168, l: "7D" }].map((r) => (
                                <button key={r.l} data-testid={`live-range-${r.l}`}
                                    onClick={() => setHistoryRange(r.h)}
                                    style={{
                                        background: historyRange === r.h ? "rgba(201,162,39,0.15)" : "transparent",
                                        border: `1px solid ${BORDER}`,
                                        color: historyRange === r.h ? GOLD : "#888",
                                        fontSize: 10, padding: "5px 10px", cursor: "pointer",
                                        letterSpacing: "0.15em", fontWeight: 600,
                                    }}>{r.l}</button>
                            ))}
                        </div>
                    </div>

                    <ResponsiveContainer width="100%" height={280}>
                        <AreaChart data={liveCurve}>
                            <defs>
                                <linearGradient id="gLive" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor={GOLD} stopOpacity={0.5} />
                                    <stop offset="100%" stopColor={GOLD} stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="1 4" stroke="#222" />
                            <XAxis dataKey="t" stroke="#555" tick={{ fontSize: 9 }}
                                tickFormatter={(t) => t ? t.slice(11, 16) : ""} minTickGap={40} />
                            <YAxis stroke="#555" tick={{ fontSize: 10 }} domain={["auto", "auto"]}
                                tickFormatter={(v) => `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`} />
                            <Tooltip contentStyle={{
                                background: "#1a1a1a", border: `1px solid ${GOLD}`, fontSize: 12, color: "#fff",
                            }} formatter={(v) => `$${Number(v).toFixed(2)}`}
                                labelFormatter={(t) => t ? `${t.slice(0, 10)} ${t.slice(11, 19)} UTC` : ""} />
                            <ReferenceLine y={stats?.total_deposits ?? (stats?.inception_capital ?? 10000)} stroke="#666" strokeDasharray="4 4" label={{
                                value: `Cost basis $${(stats?.total_deposits ?? (stats?.inception_capital ?? 10000)).toLocaleString(undefined, { maximumFractionDigits: 0 })}`, position: "insideTopRight", fill: "#666", fontSize: 9,
                            }} />
                            <Area type="monotone" dataKey="equity" stroke={GOLD} strokeWidth={2} fill="url(#gLive)" isAnimationActive={false} />
                        </AreaChart>
                    </ResponsiveContainer>

                    {liveCurve.length === 0 && (
                        <div style={{ textAlign: "center", color: "#666", fontSize: 11, padding: "40px 20px" }}>
                            Chart populates as your bot streams live snapshots. Give it 60 seconds.
                        </div>
                    )}
                </Card>

                {/* FUNDING ARB — bulletproof card (all destructured with defaults) */}
                {(() => {
                    const snap = live?.snapshot || {};
                    const fa = snap?.funding_arb;
                    if (!fa || typeof fa !== "object") return null;
                    const status = typeof fa.status === "string" ? fa.status : "unknown";
                    const cls = typeof fa.class === "string" ? fa.class : "FundingArbStrategy";
                    const capital = Number(fa.capital) || 0;
                    const rx = Number(fa.total_funding_received) || 0;
                    const pnl = Number(fa.funding_pnl) || 0;
                    const positions = Number(fa.positions_count) || 0;
                    const ticks = Number(fa.tick_count) || 0;
                    return (
                        <Card style={{ padding: 20 }} data-testid="funding-arb-card">
                            <Kicker>Layer 2 · Funding Rate Arbitrage</Kicker>
                            <h2 style={{ fontFamily: "Playfair Display, serif", color: "#fff", fontSize: 18, fontWeight: 400, marginBottom: 4 }}>
                                Funding Arb Engine
                            </h2>
                            <div style={{ color: "#666", fontSize: 10, marginBottom: 14, lineHeight: 1.5 }}>
                                {cls} · paper burn-in · runs isolated from grid
                            </div>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                                <div>
                                    <div style={{ color: "#666", fontSize: 9, textTransform: "uppercase", letterSpacing: 1 }}>Status</div>
                                    <div style={{ color: status === "running" ? "#4ade80" : "#f59e0b", fontSize: 14, fontWeight: 500, marginTop: 4 }} data-testid="funding-arb-status">
                                        {"● " + status.toUpperCase()}
                                    </div>
                                </div>
                                <div>
                                    <div style={{ color: "#666", fontSize: 9, textTransform: "uppercase", letterSpacing: 1 }}>Capital allocated</div>
                                    <div style={{ color: "#fff", fontSize: 14, fontWeight: 500, marginTop: 4 }} data-testid="funding-arb-capital">
                                        {"$" + capital.toFixed(0)}
                                    </div>
                                </div>
                                <div>
                                    <div style={{ color: "#666", fontSize: 9, textTransform: "uppercase", letterSpacing: 1 }}>Funding received (lifetime raw) 🔒</div>
                                    <div style={{ color: rx > 0 ? "#4ade80" : "#666", fontSize: 20, fontWeight: 300, marginTop: 4 }} data-testid="funding-arb-total-received">
                                        {"$" + rx.toFixed(4)}
                                    </div>
                                </div>
                                <div>
                                    <div style={{ color: "#666", fontSize: 9, textTransform: "uppercase", letterSpacing: 1 }}>Funding PnL (since fresh start)</div>
                                    <div style={{ color: pnl > 0 ? "#4ade80" : pnl < 0 ? "#ef4444" : "#666", fontSize: 20, fontWeight: 300, marginTop: 4 }} data-testid="funding-arb-pnl">
                                        {(pnl >= 0 ? "+$" : "−$") + Math.abs(pnl).toFixed(4)}
                                    </div>
                                </div>
                                <div>
                                    <div style={{ color: "#666", fontSize: 9, textTransform: "uppercase", letterSpacing: 1 }}>Positions open</div>
                                    <div style={{ color: "#fff", fontSize: 14, fontWeight: 500, marginTop: 4 }} data-testid="funding-arb-positions">
                                        {positions}
                                    </div>
                                </div>
                                <div>
                                    <div style={{ color: "#666", fontSize: 9, textTransform: "uppercase", letterSpacing: 1 }}>Ticks completed</div>
                                    <div style={{ color: "#fff", fontSize: 14, fontWeight: 500, marginTop: 4 }} data-testid="funding-arb-ticks">
                                        {ticks}
                                    </div>
                                </div>
                            </div>
                            <div style={{ marginTop: 14, padding: "8px 12px", background: "rgba(180, 140, 60, 0.08)", border: "1px solid rgba(180, 140, 60, 0.2)", borderRadius: 4, fontSize: 10, color: "#a58a4b", lineHeight: 1.5 }}>
                                Fresh start: {fa.fresh_start_date || "29 Jul 2026"} · entry threshold raised to 12% APY.
                                Only high-APY funding windows will open a delta-neutral pair. Historical burn-in P&L archived in trade log.
                            </div>
                        </Card>
                    );
                })()}

                {/* PATH TO 30% — with gap-closing strategies */}
                <Card style={{ padding: 20 }}>
                    <Kicker>Return Roadmap · Path to 30%/yr</Kicker>
                    <h2 style={{ fontFamily: "Playfair Display, serif", color: "#fff", fontSize: 18, fontWeight: 400, marginBottom: 4 }}>
                        Six-Layer Return Stack
                    </h2>
                    <div style={{ color: "#666", fontSize: 10, marginBottom: 14, lineHeight: 1.5 }}>
                        Layers 1-4 backtested. Layers 5-6 planned to close the 5.6% gap to 30%.
                    </div>
                    {[
                        { name: "Base Grid · BTC/ETH/SOL/XRP/INJ", value: 10.4, done: true, note: "LIVE — 5-asset · Martyn LP $10K tranche · 13 Aug 2026" },
                        { name: "Funding Rate Arb · Bybit", value: 9.9, done: true, note: "LIVE — Martyn tranche · Bybit UTA · running clean since 29 Jul" },
                        { name: "Bybit USDC Earn · L3", value: 2.4, done: true, note: "LIVE — $496.55 subscribed to Bybit Flexible Savings · ~4% APY" },
                        { name: "Bybit ETH Staking · L4", value: 1.7, done: true, note: "LIVE — 0.5 stETH staked via Bybit Earn · ~3.5% APY (Lido-backed)" },
                        { name: "Cash-and-Carry Basis (L6)", value: 3.1, done: true, note: "ARMED — installed 13 Aug 2026, basis-hunt watching" },
                        { name: "Bybit USDC Options (L7)", value: 2.5, done: true, note: "ARMED — institutional venue (2nd deepest options book, #1 for ETH)" },
                    ].map((layer, i) => (
                        <div key={i} style={{ marginBottom: 10 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, marginBottom: 3 }}>
                                <span style={{ color: layer.done ? GREEN : "#eab308" }}>
                                    {layer.done ? "✓ " : "○ "}{layer.name}
                                </span>
                                <span style={{ color: GOLD, fontWeight: 600 }}>+{layer.value}%</span>
                            </div>
                            {layer.note && (
                                <div style={{ color: "#666", fontSize: 9, marginBottom: 3, fontStyle: "italic" }}>
                                    {layer.note}
                                </div>
                            )}
                            <div style={{ height: 3, background: "#222", borderRadius: 2 }}>
                                <div style={{
                                    height: "100%", width: `${(layer.value / 15) * 100}%`,
                                    background: layer.done ? GREEN : "#eab308", opacity: layer.done ? 1 : 0.7,
                                    borderRadius: 2,
                                }} />
                            </div>
                        </div>
                    ))}
                    <div style={{ borderTop: `1px solid ${BORDER}`, marginTop: 14, paddingTop: 10 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11 }}>
                            <span style={{ color: "#aaa" }}>Blended Target:</span>
                            <span style={{ color: GOLD, fontSize: 16, fontFamily: "Playfair Display, serif" }}>30.0%/yr</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginTop: 4 }}>
                            <span style={{ color: "#aaa" }}>Backtest Baseline:</span>
                            <span style={{ color: GREEN, fontSize: 14, fontWeight: 600 }}>24.4%/yr</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginTop: 4 }}>
                            <span style={{ color: "#aaa" }}>Gap to Close:</span>
                            <span style={{ color: "#eab308", fontSize: 14, fontWeight: 600 }}>+5.6% (Layers 5-6)</span>
                        </div>
                        <div style={{ fontSize: 9.5, color: "#6b7280", marginTop: 8, fontStyle: "italic", lineHeight: 1.5 }}>
                            Baseline from 5-year historical backtest · live proof accumulates over 90d rolling window.
                        </div>
                    </div>
                </Card>
            </div>

            {/* ASSET BREAKDOWN — LIVE from bot */}
            {(() => {
                const isLive = live?.live && live?.snapshot;
                const assetsMap = live?.snapshot?.assets || {};
                // COST BASIS = total deposits (inception + all capital events)
                const startCap = stats?.total_deposits ?? (stats?.inception_capital ?? BOT_CONFIG.STARTING_CAPITAL);
                // Compute alloc% dynamically from equity share so it works for any N assets
                const totalStart = Object.values(assetsMap).reduce((s, a) => {
                    const r = (a.return_pct || 0) / 100;
                    return s + (a.equity || 0) / (1 + r);
                }, 0) || startCap;

                return (
                    <Card style={{ marginBottom: 24 }}>
                        <Kicker>Per-Asset Performance · Live</Kicker>
                        <h2 style={{ fontFamily: "Playfair Display, serif", color: "#fff", fontSize: 20, fontWeight: 400, marginBottom: 16 }}>
                            Positions & Regime Detection
                        </h2>
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                                <tr style={{ borderBottom: `1px solid ${BORDER}` }}>
                                    {["Asset", "Alloc", "Started", "Current Equity", "Return", "Fills", "Open Orders", "Regime", "Status"].map((h) => (
                                        <th key={h} style={{
                                            textAlign: "left", padding: "8px 12px", color: GOLD,
                                            fontSize: 9, letterSpacing: "0.2em", textTransform: "uppercase",
                                            fontWeight: 500,
                                        }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {!isLive && (
                                    <tr>
                                        <td colSpan={9} style={{ padding: 24, textAlign: "center", color: "#666", fontSize: 12 }}>
                                            Waiting for bot data — start <code style={{ color: GOLD }}>sync_dashboard.py</code> on your Mac.
                                        </td>
                                    </tr>
                                )}
                                {isLive && Object.entries(assetsMap).map(([sym, a]) => {
                                    const r = (a.return_pct || 0) / 100;
                                    const started = (a.equity || 0) / (1 + r);
                                    const alloc = totalStart > 0 ? (started / totalStart) * 100 : 0;
                                    const status = a.is_killed ? "killed" : a.is_paused ? "paused" : "active";
                                    const mode = a.active_mode || "grid";
                                    const modeLabel = { grid: "Grid", trend_hodl: "Trend-HODL", defensive: "Defensive", flash_halt: "Halt", standard: "Grid" }[mode] || mode;
                                    const modeColor = mode === "trend_hodl" ? GREEN : mode === "defensive" || mode === "flash_halt" ? RED : GOLD;
                                    return (
                                        <tr key={sym} style={{ borderBottom: `1px solid rgba(255,255,255,0.03)` }}
                                            data-testid={`asset-row-${sym.replace("/", "-")}`}>
                                            <td style={{ padding: 12, fontSize: 13, color: "#fff", fontWeight: 500 }}>{sym}</td>
                                            <td style={{ padding: 12, fontSize: 12, color: "#aaa" }}>{alloc.toFixed(1)}%</td>
                                            <td style={{ padding: 12, fontSize: 12, color: "#aaa" }}>${started.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</td>
                                            <td style={{ padding: 12, fontSize: 13, color: "#fff", fontWeight: 500 }}>
                                                ${(a.equity || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </td>
                                            <td style={{ padding: 12, fontSize: 13, color: flatColor(a.return_pct || 0, 0.1), fontWeight: 600 }}>
                                                {(a.return_pct || 0) >= 0 ? "+" : ""}{(a.return_pct || 0).toFixed(4)}%
                                            </td>
                                            <td style={{ padding: 12, fontSize: 12, color: ((fills?.per_asset?.[sym]) ?? (a.trades_completed || 0)) > 0 ? GREEN : "#aaa", fontWeight: 600 }}>{(fills?.per_asset?.[sym]) ?? (a.trades_completed || 0)}</td>
                                            <td style={{ padding: 12, fontSize: 12, color: GOLD }}>{a.open_grid_levels || 0}</td>
                                            <td style={{ padding: 12, fontSize: 11, color: GOLD, textTransform: "capitalize" }}>{a.regime || "—"}</td>
                                            <td style={{ padding: 12 }}><StatusPill status={status} /></td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </Card>
                );
            })()}

            {/* SAFETY SYSTEMS — driven by live snapshot */}
            {(() => {
                const isLive = live?.live && live?.snapshot;
                const snap = live?.snapshot || {};
                const isHalted = snap.halted === true;
                const assetsMap = snap.assets || {};
                const anyPaused = Object.values(assetsMap).some((a) => a.is_paused);
                const anyKilled = Object.values(assetsMap).some((a) => a.is_killed);
                const regimes = [...new Set(Object.values(assetsMap).map((a) => a.regime).filter(Boolean))];
                const regimeLabel = regimes.length === 0 ? "—" : regimes.length === 1 ? regimes[0] : "mixed";

                const cards = [
                    {
                        icon: Shield, label: "Protective Liquidator",
                        status: isHalted ? "HALTED" : isLive ? "Armed" : "—",
                        detail: isHalted ? "manual/auto resume required" : "hard cap @ -5% portfolio DD",
                        ok: !isHalted && isLive,
                    },
                    {
                        icon: AlertTriangle, label: "Grid Pause",
                        status: anyPaused || anyKilled ? (anyKilled ? "STOPPED" : "PAUSED") : isLive ? "Ready" : "—",
                        detail: "-3% DD alert · per-asset guard",
                        ok: !anyPaused && !anyKilled && isLive,
                    },
                    {
                        icon: Zap, label: "Bot Mode",
                        status: isLive ? (live.snapshot.mode || "paper").toUpperCase() : "OFFLINE",
                        detail: isLive ? "Bybit UTA · LIVE mode · Martyn tranche" : "start bot on your Mac",
                        ok: isLive,
                    },
                    {
                        icon: Cpu, label: "Regime Detector",
                        status: regimeLabel,
                        detail: isLive ? `${Object.keys(assetsMap).length} assets tracked` : "—",
                        ok: isLive,
                    },
                ];
                return (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 24 }}>
                        {cards.map((s, i) => (
                            <Card key={i} data-testid={`safety-${s.label.toLowerCase().replace(/ /g, "-")}`}>
                                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                                    <s.icon size={16} color={s.ok ? GREEN : RED} />
                                    <span style={{ color: "#fff", fontSize: 12, fontWeight: 500 }}>{s.label}</span>
                                </div>
                                <div style={{ color: s.ok ? GREEN : RED, fontSize: 15, fontWeight: 600, marginBottom: 4, textTransform: "capitalize" }}>
                                    {s.status}
                                </div>
                                <div style={{ color: "#666", fontSize: 10 }}>{s.detail}</div>
                            </Card>
                        ))}
                    </div>
                );
            })()}

            {/* ═══════════════════════════════════════════════════════════ */}
            {/* COPY TRADING · MIDAS TOUCH · Master Product Profile         */}
            {/* Public listing block — powered by /api/bot/stats             */}
            {/* Zero backend touch. Read-only from existing endpoints.       */}
            {/* ═══════════════════════════════════════════════════════════ */}
            {(() => {
                const totalReturnPct = stats?.total_return_pct ?? null;
                const maxDDpct = stats?.max_drawdown_pct ?? null;
                const daysLive = stats?.days_tracked ?? null;
                const winRate = stats?.win_rate_pct ?? null;
                const sharpeReady = stats?.sharpe_ready ?? false;
                const sharpe = stats?.annualized_sharpe;
                const sharpeSamples = stats?.sharpe_samples_have ?? 0;
                const sharpeMin = stats?.sharpe_min_samples ?? 14;

                const fmtPct = (v) => v === null || v === undefined ? "—" : `${v >= 0 ? "+" : ""}${v.toFixed(2)}%`;
                const fmtNum = (v) => v === null || v === undefined ? "—" : v;

                const perfBlocks = [
                    { label: "Total Return", value: fmtPct(totalReturnPct), color: totalReturnPct >= 0 ? GREEN : RED },
                    { label: "Max Drawdown", value: fmtPct(maxDDpct), color: (maxDDpct ?? 0) >= -5 ? GREEN : RED },
                    { label: "Days Live", value: fmtNum(daysLive), color: "#fff" },
                    { label: "Win Rate", value: winRate ? `${winRate.toFixed(1)}%` : "—", color: winRate >= 50 ? GREEN : "#fff" },
                    { label: "Sharpe", value: sharpeReady ? sharpe?.toFixed(2) : `${sharpeSamples}/${sharpeMin}`, color: sharpeReady ? GREEN : "#888" },
                ];

                return (
                    <div style={{ marginBottom: 28 }} data-testid="copy-trading-section">
                        {/* Section header */}
                        <div style={{
                            display: "flex", alignItems: "center", justifyContent: "space-between",
                            marginBottom: 14, paddingBottom: 12, borderBottom: `1px solid ${BORDER}`,
                        }}>
                            <div>
                                <div style={{
                                    color: GOLD, fontSize: 10, letterSpacing: "0.25em",
                                    textTransform: "uppercase", fontWeight: 500,
                                }}>Copy Trading</div>
                                <div style={{
                                    color: "#fff", fontSize: 20, fontWeight: 300,
                                    letterSpacing: "0.02em", marginTop: 4,
                                }}>Follower Product</div>
                            </div>
                            <div style={{
                                fontSize: 10, color: GOLD, letterSpacing: "0.2em",
                                textTransform: "uppercase", opacity: 0.7,
                            }}>Bybit Master Trader</div>
                        </div>

                        {/* Section 1 — Midas Touch Master Profile Card */}
                        <Card glow data-testid="ct-master-profile">
                            {/* Header row */}
                            <div style={{
                                display: "flex", alignItems: "flex-start", justifyContent: "space-between",
                                marginBottom: 24, paddingBottom: 18, borderBottom: `1px solid ${BORDER}`,
                            }}>
                                <div>
                                    <div style={{
                                        color: GOLD, fontSize: 10, letterSpacing: "0.25em",
                                        textTransform: "uppercase", marginBottom: 8,
                                    }}>Product Listing</div>
                                    <div style={{
                                        color: "#fff", fontSize: 32, fontWeight: 300,
                                        letterSpacing: "0.02em", lineHeight: 1.1,
                                    }} data-testid="ct-product-name">Midas Touch</div>
                                    <div style={{
                                        color: "#888", fontSize: 13, marginTop: 8, letterSpacing: "0.02em",
                                    }}>Low-Risk Grid + Yield Stack · 7-Layer Defense</div>
                                </div>
                                <div style={{ textAlign: "right" }}>
                                    <div style={{
                                        color: GOLD, fontSize: 10, letterSpacing: "0.2em",
                                        textTransform: "uppercase", marginBottom: 6,
                                    }}>Category</div>
                                    <div style={{
                                        color: "#fff", fontSize: 14, fontWeight: 500,
                                    }}>Institutional Safe Layer</div>
                                    <div style={{
                                        color: "#666", fontSize: 10, marginTop: 6, letterSpacing: "0.15em",
                                        textTransform: "uppercase",
                                    }}>Grid + Yield · Delta-Neutral</div>
                                </div>
                            </div>

                            {/* Live performance grid */}
                            <div style={{ marginBottom: 20 }}>
                                <div style={{
                                    color: GOLD, fontSize: 10, letterSpacing: "0.2em",
                                    textTransform: "uppercase", marginBottom: 12, opacity: 0.8,
                                }}>Live Performance</div>
                                <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 12 }}>
                                    {perfBlocks.map((b) => (
                                        <div key={b.label} style={{
                                            background: "#0f0f0f", border: `1px solid ${BORDER}`,
                                            borderRadius: 4, padding: "14px 12px",
                                        }} data-testid={`ct-stat-${b.label.toLowerCase().replace(/ /g, "-")}`}>
                                            <div style={{
                                                color: "#666", fontSize: 9, letterSpacing: "0.2em",
                                                textTransform: "uppercase", marginBottom: 8,
                                            }}>{b.label}</div>
                                            <div style={{
                                                color: b.color, fontSize: 18, fontWeight: 500,
                                                letterSpacing: "0.01em",
                                            }}>{b.value}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Strategy summary */}
                            <div style={{
                                background: "#0f0f0f", border: `1px solid ${BORDER}`,
                                borderRadius: 4, padding: 16,
                            }}>
                                <div style={{
                                    color: GOLD, fontSize: 10, letterSpacing: "0.2em",
                                    textTransform: "uppercase", marginBottom: 10, opacity: 0.8,
                                }}>Strategy Summary</div>
                                <div style={{
                                    display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8,
                                    color: "#aaa", fontSize: 12, lineHeight: 1.6,
                                }}>
                                    <div>• 5-asset grid trading (BTC · ETH · SOL · XRP · INJ)</div>
                                    <div>• Delta-neutral funding arbitrage overlay</div>
                                    <div>• USDC + stETH yield on idle capital</div>
                                    <div>• Cash-and-carry basis trades on contango</div>
                                    <div>• BEAR-gated cash-secured PUT options</div>
                                    <div>• 7-layer defense · kill switch at −5% NAV</div>
                                </div>
                            </div>

                            {/* Footer badge row */}
                            <div style={{
                                display: "flex", justifyContent: "space-between", alignItems: "center",
                                marginTop: 18, paddingTop: 14, borderTop: `1px solid ${BORDER}`,
                                fontSize: 10, color: "#666", letterSpacing: "0.15em", textTransform: "uppercase",
                            }}>
                                <span>Live · Bybit UTA · Mainnet</span>
                                <span style={{ color: GOLD }}>Since 13 Aug 2026</span>
                                <span>Zero Leverage</span>
                            </div>
                        </Card>

                        {/* ─── Section 2 · Risk Disclosure Panel ─── */}
                        <Card style={{ marginTop: 16 }} data-testid="ct-risk-disclosure">
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, paddingBottom: 12, borderBottom: `1px solid ${BORDER}` }}>
                                <div>
                                    <div style={{ color: GOLD, fontSize: 10, letterSpacing: "0.25em", textTransform: "uppercase", marginBottom: 6 }}>Risk Disclosure</div>
                                    <div style={{ color: "#fff", fontSize: 18, fontWeight: 400 }}>Follower Protection Framework</div>
                                </div>
                                <Shield size={22} color={GOLD} />
                            </div>
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 16 }}>
                                {[
                                    { label: "Max DD Target", value: "< 5%", detail: "Portfolio-wide", color: GREEN },
                                    { label: "Kill Switch", value: "−5% NAV", detail: "Auto → USDT", color: GOLD },
                                    { label: "Blended APR", value: "30% target", detail: "40%+ great year", color: GREEN },
                                    { label: "Leverage", value: "None", detail: "Spot + delta-neutral", color: "#fff" },
                                ].map((b) => (
                                    <div key={b.label} style={{ background: "#0f0f0f", border: `1px solid ${BORDER}`, borderRadius: 4, padding: 14 }}>
                                        <div style={{ color: "#666", fontSize: 9, letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 6 }}>{b.label}</div>
                                        <div style={{ color: b.color, fontSize: 17, fontWeight: 500, marginBottom: 4 }}>{b.value}</div>
                                        <div style={{ color: "#666", fontSize: 10 }}>{b.detail}</div>
                                    </div>
                                ))}
                            </div>
                            <div style={{ marginTop: 4, color: "#666", fontSize: 10, letterSpacing: "0.1em", textAlign: "center", fontStyle: "italic" }}>
                                Past performance does not guarantee future results. Copy trading involves risk of capital loss.
                            </div>
                        </Card>

                        {/* ─── Section 3 · Follower Onboarding Flow ─── */}
                        <Card style={{ marginTop: 16 }} data-testid="ct-onboarding">
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, paddingBottom: 12, borderBottom: `1px solid ${BORDER}` }}>
                                <div>
                                    <div style={{ color: GOLD, fontSize: 10, letterSpacing: "0.25em", textTransform: "uppercase", marginBottom: 6 }}>Follower Onboarding</div>
                                    <div style={{ color: "#fff", fontSize: 18, fontWeight: 400 }}>How to Copy Midas Touch</div>
                                </div>
                                <Zap size={22} color={GOLD} />
                            </div>
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10 }}>
                                {[
                                    { step: "01", title: "Create Bybit Account", body: "Sign up via our referral link (activated post approval)" },
                                    { step: "02", title: "Deposit USDT", body: "Minimum $500 · Recommended $2,000+ for meaningful grid" },
                                    { step: "03", title: "Find Midas Touch", body: "In Bybit Copy Trading marketplace, search 'Midas Touch'" },
                                    { step: "04", title: "Set Follow Amount", body: "50-100% of deposit recommended · isolated per-follower" },
                                    { step: "05", title: "Auto-Mirror Live", body: "Bybit mirrors every trade · weekly Friday settlement" },
                                ].map((s) => (
                                    <div key={s.step} style={{ background: "#0f0f0f", border: `1px solid ${BORDER}`, borderRadius: 4, padding: 14 }}>
                                        <div style={{ color: GOLD, fontSize: 20, fontWeight: 300, marginBottom: 8, letterSpacing: "0.05em" }}>{s.step}</div>
                                        <div style={{ color: "#fff", fontSize: 12, fontWeight: 500, marginBottom: 6 }}>{s.title}</div>
                                        <div style={{ color: "#888", fontSize: 11, lineHeight: 1.5 }}>{s.body}</div>
                                    </div>
                                ))}
                            </div>
                            <div style={{ marginTop: 14, padding: 12, background: "rgba(201,162,39,0.06)", border: `1px solid ${BORDER}`, borderRadius: 4, color: "#aaa", fontSize: 11, textAlign: "center" }}>
                                Public referral link goes live after Bybit Master Trader approval · Target: 12 Sep 2026 (30-day track mark)
                            </div>
                        </Card>

                        {/* ─── Section 4 · Fee & Revenue Model ─── */}
                        <Card style={{ marginTop: 16 }} data-testid="ct-fee-model">
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, paddingBottom: 12, borderBottom: `1px solid ${BORDER}` }}>
                                <div>
                                    <div style={{ color: GOLD, fontSize: 10, letterSpacing: "0.25em", textTransform: "uppercase", marginBottom: 6 }}>Fee Model</div>
                                    <div style={{ color: "#fff", fontSize: 18, fontWeight: 400 }}>70 / 30 Profit Split</div>
                                </div>
                                <Percent size={22} color={GOLD} />
                            </div>
                            <div style={{ display: "grid", gridTemplateColumns: "1.2fr 0.8fr", gap: 14, marginBottom: 14 }}>
                                <div style={{ background: "#0f0f0f", border: `1px solid ${BORDER}`, borderRadius: 4, padding: 18 }}>
                                    <div style={{ color: GOLD, fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 12 }}>Split Structure</div>
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", borderBottom: `1px solid ${BORDER}` }}>
                                        <span style={{ color: "#fff", fontSize: 13 }}>Follower keeps</span>
                                        <span style={{ color: GREEN, fontSize: 22, fontWeight: 500 }}>70%</span>
                                    </div>
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", borderBottom: `1px solid ${BORDER}` }}>
                                        <span style={{ color: "#fff", fontSize: 13 }}>Guild fee</span>
                                        <span style={{ color: GOLD, fontSize: 22, fontWeight: 500 }}>30%</span>
                                    </div>
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0" }}>
                                        <span style={{ color: "#888", fontSize: 12 }}>Settlement</span>
                                        <span style={{ color: "#fff", fontSize: 13 }}>Weekly · Friday</span>
                                    </div>
                                    <div style={{ color: "#666", fontSize: 10, marginTop: 12, lineHeight: 1.6 }}>
                                        Guild fee applies to REALIZED profit only. No fees on principal, unrealized gains, or losses.
                                    </div>
                                </div>
                                <div style={{ background: "#0f0f0f", border: `1px solid ${BORDER}`, borderRadius: 4, padding: 18 }}>
                                    <div style={{ color: GOLD, fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 12 }}>Example Week</div>
                                    <div style={{ color: "#888", fontSize: 11, marginBottom: 6 }}>Your capital</div>
                                    <div style={{ color: "#fff", fontSize: 20, fontWeight: 500, marginBottom: 14 }}>$10,000</div>
                                    <div style={{ color: "#888", fontSize: 11, marginBottom: 6 }}>Realized profit this week</div>
                                    <div style={{ color: "#fff", fontSize: 20, fontWeight: 500, marginBottom: 14 }}>+$500</div>
                                    <div style={{ borderTop: `1px solid ${BORDER}`, paddingTop: 12 }}>
                                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
                                            <span style={{ color: GREEN }}>You keep</span>
                                            <span style={{ color: GREEN, fontWeight: 500 }}>$350</span>
                                        </div>
                                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginTop: 4 }}>
                                            <span style={{ color: GOLD }}>Guild</span>
                                            <span style={{ color: GOLD, fontWeight: 500 }}>$150</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Card>

                        {/* ─── Section 5 · Application Status (admin visible) ─── */}
                        <Card style={{ marginTop: 16 }} data-testid="ct-application-status">
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, paddingBottom: 12, borderBottom: `1px solid ${BORDER}` }}>
                                <div>
                                    <div style={{ color: GOLD, fontSize: 10, letterSpacing: "0.25em", textTransform: "uppercase", marginBottom: 6 }}>Application Status</div>
                                    <div style={{ color: "#fff", fontSize: 18, fontWeight: 400 }}>Bybit Master Trader Submission</div>
                                </div>
                                <div style={{ padding: "6px 12px", background: "rgba(201,162,39,0.12)", border: `1px solid ${BORDER}`, borderRadius: 4, color: GOLD, fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase" }}>
                                    Draft · Pending Info
                                </div>
                            </div>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                                <div style={{ background: "#0f0f0f", border: `1px solid ${BORDER}`, borderRadius: 4, padding: 16 }}>
                                    <div style={{ color: GOLD, fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 10 }}>Ready to Submit</div>
                                    {["Product listing: Midas Touch", "Category: Institutional Safe Layer", "Live track record: 11 days · +10.20% · MaxDD −2.07%", "Risk disclosure framework", "Fee split model (70 / 30)", "Onboarding flow documented"].map((item) => (
                                        <div key={item} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0", fontSize: 11, color: "#aaa" }}>
                                            <span style={{ color: GREEN, fontSize: 12 }}>✓</span>
                                            <span>{item}</span>
                                        </div>
                                    ))}
                                </div>
                                <div style={{ background: "#0f0f0f", border: `1px solid ${BORDER}`, borderRadius: 4, padding: 16 }}>
                                    <div style={{ color: GOLD, fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 10 }}>Pending Info</div>
                                    {["Legal entity name", "Guild fee routing wallet / bank", "Compliance jurisdiction", "Director KYC (Yusuf provides)"].map((item) => (
                                        <div key={item} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0", fontSize: 11, color: "#aaa" }}>
                                            <span style={{ color: GOLD, fontSize: 12 }}>☐</span>
                                            <span>{item}</span>
                                        </div>
                                    ))}
                                    <div style={{ marginTop: 12, padding: 10, background: "rgba(201,162,39,0.06)", borderRadius: 4, color: "#aaa", fontSize: 10, lineHeight: 1.5 }}>
                                        Yusuf to provide Wed 26 Aug 2026 at office meeting. Application submits same day. Public launch target: 12 Sep 2026.
                                    </div>
                                </div>
                            </div>
                        </Card>
                    </div>
                );
            })()}

            {/* FOOTER */}
            <div style={{
                borderTop: `1px solid ${BORDER}`, paddingTop: 20, marginTop: 8,
                display: "flex", justifyContent: "space-between", fontSize: 10,
                color: "#666", letterSpacing: "0.15em", textTransform: "uppercase",
            }}>
                <span>LIVE TRADING · Bybit UTA · Martyn LP $10K</span>
                <span style={{ color: GOLD }}>Live data from your Mac · Backtest shown for reference</span>
                <span>v3.0 Foolproof · Multi-Asset</span>
            </div>
        </div>
    );
}
